import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { List as VirtualList } from "react-window";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../lib/useIsMobile";
import MobileTabDropdown from "../components/MobileTabDropdown";
import MobileDashboardCards, { fmtCompact as mfmtCompact } from "../components/MobileDashboardCards";
import TablePagination from "../components/TablePagination";
import WelcomeTour from "../components/WelcomeTour";
import { useHiddenCards } from "../lib/cardPreferences";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Users,
  TrendingUp,
  IndianRupee,
  PieChart as PieChartIcon,
  Calendar,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Check,
  Building2,
  User,
  Briefcase,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  ArrowRightLeft,
  FileText,
  Search,
  GitBranch,
  ChevronRight,
  ChevronDown,
  Crown,
  ExternalLink,
  AlertCircle,
  Target,
  DollarSign,
  FileWarning,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import * as XLSX from "xlsx";
import { exportStyledXlsx } from "../lib/excelExport";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colors for pie chart
const PIE_COLORS = ['#0d9488', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#2563eb'];

// Helper to format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "-";
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} L`;
  }
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

// Helper to convert text to Title Case
// Shared name formatter — turns "ASHWINI RAJAN ATHALYE" into "Ashwini Rajan
// Athalye" and is taught about honorifics, suffixes, Mc-prefixed surnames and
// apostrophes. Lives in `lib/formatName` so every page on the frontend renders
// consistent casing by default.
import { toTitleCase } from "../lib/formatName";

export default function Dashboard() {
  const { token, api, user } = useAuth();
  const isSubBroker = user?.role === "sub_broker";

  // ── Pagination state (Phase-3 UX enhancement 2026-02-11) ──────────────
  // Client-side pagination for every Dashboard tab that renders a list.
  // Kept as one bundle so tabs share the same page-size preference feel.
  const [pgSize, setPgSize] = useState(50);
  const [pgExited, setPgExited] = useState(1);
  const [pgTransferOut, setPgTransferOut] = useState(1);
  const [pgTransmission, setPgTransmission] = useState(1);
  const [pgMultiBroker, setPgMultiBroker] = useState(1);
  const [pgReducedPortfolio, setPgReducedPortfolio] = useState(1);
  const [pgUnderperformers, setPgUnderperformers] = useState(1);
  const [pgOutperformers, setPgOutperformers] = useState(1);
  const [pgElssPending, setPgElssPending] = useState(1);
  const [pgProfitRealignment, setPgProfitRealignment] = useState(1);
  const [pgUnpaidBrokerage, setPgUnpaidBrokerage] = useState(1);
  const [pgHistorical, setPgHistorical] = useState(1);
  // Cache freshness indicator — read by /api/dashboard/cache-status,
  // shown in the header so the user knows how stale the current view is.
  const [cacheStatus, setCacheStatus] = useState(null);
  const [pgMismatch, setPgMismatch] = useState(1);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { hidden: hiddenCards } = useHiddenCards("dashboard");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [importStatus, setImportStatus] = useState(null); // Import status from API
  const [recentEntries, setRecentEntries] = useState(null); // Recent entries for verification
  const [showRecentEntries, setShowRecentEntries] = useState(false); // Toggle for recent entries
  const [loadingRecentEntries, setLoadingRecentEntries] = useState(false);

  // Sorting state for tables
  const [xirrSort, setXirrSort] = useState({ column: null, direction: 'asc' });
  const [amcSort, setAmcSort] = useState({ column: null, direction: 'asc' });
  const [exitedSort, setExitedSort] = useState({ column: 'redeemed_amount', direction: 'desc' });
  const [transferOutSort, setTransferOutSort] = useState({ column: 'invested_amount', direction: 'desc' });
  const [transmissionSort, setTransmissionSort] = useState({ column: 'invested_amount', direction: 'desc' });

  // Search state for exited tabs
  const [exitedSearch, setExitedSearch] = useState('');
  const [transferOutSearch, setTransferOutSearch] = useState('');
  const [transmissionSearch, setTransmissionSearch] = useState('');
  const [multiBrokerSearch, setMultiBrokerSearch] = useState('');
  
  // Sort state for new tabs
  const [newTransferOutSort, setNewTransferOutSort] = useState({ column: 'active_units', direction: 'desc' });
  const [newTransmissionSort, setNewTransmissionSort] = useState({ column: 'active_units', direction: 'desc' });
  const [multiBrokerSort, setMultiBrokerSort] = useState({ column: 'other_broker_units', direction: 'desc' });
  const [expandedMultiBroker, setExpandedMultiBroker] = useState(null);  // PAN currently expanded for folio drill-down
  const [expandedMismatch, setExpandedMismatch] = useState(null);  // 2026-07-05: PAN-wise Mismatch drill-down

  // Reduced Portfolio + Underperformers tab state
  const [reducedSearch, setReducedSearch] = useState('');
  const [reducedSort, setReducedSort] = useState({ column: 'drawdown_amount', direction: 'desc' });
  const [underperformers, setUnderperformers] = useState(null);
  const [underperformersLoading, setUnderperformersLoading] = useState(false);
  const [underSearch, setUnderSearch] = useState('');
  const [underSort, setUnderSort] = useState({ column: 'weighted_xirr_pct', direction: 'asc' });
  // User-configurable filters — default = holding ≥ 5 years AND XIRR < 14%.
  // Colors band the result: <11% red, 11-14% yellow.
  const [underMinYears, setUnderMinYears] = useState(3);
  const [underMaxXirr, setUnderMaxXirr] = useState(14);

  // Outperformers — mirror of underperformers, flipped (XIRR ≥ 14%).
  // Default sort = highest absolute P/L first (NOT XIRR) so the headline
  // ranking matches the "best performers" mental model. XIRR alone
  // surfaces tiny-cost high-percentage rows that aren't actionable.
  const [outperformers, setOutperformers] = useState(null);
  const [outperformersLoading, setOutperformersLoading] = useState(false);
  const [outSearch, setOutSearch] = useState('');
  const [outSort, setOutSort] = useState({ column: 'total_profit_loss', direction: 'desc' });

  // Historical Data — folios where Transfer In was detected but no
  // matching WBR2A historical transactions exist (means the broker never
  // uploaded the buy-side history that explains where those units came
  // from). Surfaces rows to backfill.
  const [historicalData, setHistoricalData] = useState(null);
  // Mismatch tab — folios where transaction_master-derived active units
  // don't match aum_master holdings, per-PAN (mirrors Portfolio > Unit
  // Mismatch). 2026-07-07 user directive.
  const [mismatchData, setMismatchData] = useState(null);
  const [mismatchLoading, setMismatchLoading] = useState(false);
  const [mismatchSearch, setMismatchSearch] = useState("");
  const [mismatchCapsuleFilter, setMismatchCapsuleFilter] = useState("all"); // all|SIP|STP|SWP|Purchase|Sell|Switch|Txn
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalSearch, setHistoricalSearch] = useState('');
  const [outMinYears, setOutMinYears] = useState(5);
  const [outMinXirr, setOutMinXirr] = useState(14);

  // ELSS Pending + Profit Realignment tabs (added 2026-02-07)
  const [elssPending, setElssPending] = useState(null);
  const [elssPendingLoading, setElssPendingLoading] = useState(false);
  const [elssSearch, setElssSearch] = useState('');
  const [elssStatusFilter, setElssStatusFilter] = useState('all');     // all | zero | partial (legacy — retained for state hygiene)
  const [elssOpenFYs, setElssOpenFYs] = useState({});  // FY-label → open state, drives the FY accordion in ELSS by FY tab
  const [elssOpenClients, setElssOpenClients] = useState({});  // "FY::PAN::idx" → open state, drives per-client scheme drilldown
  const [profitRealignment, setProfitRealignment] = useState(null);
  const [profitRealignmentLoading, setProfitRealignmentLoading] = useState(false);
  // Portfolio Mix (Equity / Debt / Hybrid bifurcation) — iteration 37
  const [portfolioMix, setPortfolioMix] = useState(null);
  const [portfolioMixLoading, setPortfolioMixLoading] = useState(false);
  const [unpaidBrokerage, setUnpaidBrokerage] = useState(null);
  const [unpaidBrokerageLoading, setUnpaidBrokerageLoading] = useState(false);
  const [unpaidSearch, setUnpaidSearch] = useState("");
  const [unpaidSort, setUnpaidSort] = useState({ column: "total_unpaid_amount", direction: "desc" });
  const [expandedUnpaidPan, setExpandedUnpaidPan] = useState({});
  const [expandedUnpaidFolio, setExpandedUnpaidFolio] = useState({});
  const [unpaidStatusFilter, setUnpaidStatusFilter] = useState("all"); // all | folio_never_paid | txn_unpaid
  // Per-(client × scheme-folio group) expand/collapse for the lot breakdown.
  const [expandedProfitGroups, setExpandedProfitGroups] = useState({});
  const [expandedProfitClient, setExpandedProfitClient] = useState({});
  const [profitSort, setProfitSort] = useState({ column: 'recommended_harvest', direction: 'desc' });
  const [profitSearch, setProfitSearch] = useState('');
  const [profitMinGain, setProfitMinGain] = useState(50000);

  // Family AUM Leaderboard tab state
  const [familyAum, setFamilyAum] = useState(null);
  const [familyAumLoading, setFamilyAumLoading] = useState(false);
  const [familySearch, setFamilySearch] = useState('');
  const [familySort, setFamilySort] = useState({ column: 'total_market', direction: 'desc' });
  const [expandedFamily, setExpandedFamily] = useState(null);  // group_head_pan currently expanded
  const [familyMinMembers, setFamilyMinMembers] = useState(1);

  // Real-time reconciliation state
  const [reconciliationRunning, setReconciliationRunning] = useState(false);
  const [reconciliationProgress, setReconciliationProgress] = useState(null);
  const [reconciliationResults, setReconciliationResults] = useState([]);
  const [reconciliationStatus, setReconciliationStatus] = useState(null); // Status counts
  const [reconciliationMode, setReconciliationMode] = useState("pending"); // pending, all, resume
  const [segregatedEntries, setSegregatedEntries] = useState([]); // Segregated portfolios - non-financial entries
  const [transferOutEntries, setTransferOutEntries] = useState([]); // Transfer out entries - holdings = 0 but purchases exist
  const [reconciliationSubTab, setReconciliationSubTab] = useState("mismatches"); // "mismatches", "segregated", or "transfer_outs"
  const [reconSortBy, setReconSortBy] = useState("difference"); // Sort field
  const [reconSortOrder, setReconSortOrder] = useState("desc"); // asc or desc

  // Drill-down state — shared across every Dashboard tab.
  // Key format: "<tab>:<pan-or-group_key>" → { loading, folios, error }
  const [drillDown, setDrillDown] = useState({});  // cached responses per client row
  // 2026-08-08 — Transmission sub-tab (Pending / Transmitted). Declared
  // up here (not later next to filteredNewTransmission) because
  // `toggleDrillDown` references it in its dep array, and JS hoisting
  // does not apply to `const` — a later declaration would trip the
  // temporal-dead-zone error `Cannot access 'transmissionSubTab'
  // before initialization` on every render.
  const [transmissionSubTab, setTransmissionSubTab] = useState('pending');
  const [openDrillKey, setOpenDrillKey] = useState(null); // currently expanded key

  // AMC Wise Breakdown drill-down (Dashboard → AMC card)
  // Two-level expand: AMC row → schemes table; scheme sub-row → clients table.
  const [expandedAmc, setExpandedAmc] = useState(null);              // canonical amc_name currently open
  const [amcSchemes, setAmcSchemes] = useState({});                   // { amc_name: { loading, data, error } }
  const [expandedScheme, setExpandedScheme] = useState(null);         // "amc_name|scheme_name" currently open
  const [schemeClients, setSchemeClients] = useState({});             // { key: { loading, data, error } }
  // Sort config for the AMC drill-down sub-tables (only one open at a time
  // → single shared state is fine and resets when the user opens a new AMC).
  const [amcSchemeSort, setAmcSchemeSort] = useState({ column: 'market_value', direction: 'desc' });
  const [schemeClientSort, setSchemeClientSort] = useState({ column: 'market_value', direction: 'desc' });

  const fetchAmcSchemes = async (amcName) => {
    if (amcSchemes[amcName]?.data || amcSchemes[amcName]?.loading) return;
    setAmcSchemes(prev => ({ ...prev, [amcName]: { loading: true } }));
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/dashboard/amc-drilldown/schemes?amc_name=${encodeURIComponent(amcName)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setAmcSchemes(prev => ({ ...prev, [amcName]: { loading: false, data: j.schemes || [] } }));
    } catch (e) {
      setAmcSchemes(prev => ({ ...prev, [amcName]: { loading: false, error: e.message || String(e) } }));
    }
  };

  const fetchSchemeClients = async (amcName, schemeName) => {
    const key = `${amcName}|${schemeName}`;
    if (schemeClients[key]?.data || schemeClients[key]?.loading) return;
    setSchemeClients(prev => ({ ...prev, [key]: { loading: true } }));
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/dashboard/amc-drilldown/clients?amc_name=${encodeURIComponent(amcName)}&scheme_name=${encodeURIComponent(schemeName)}`;
      const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setSchemeClients(prev => ({ ...prev, [key]: { loading: false, data: j.clients || [] } }));
    } catch (e) {
      setSchemeClients(prev => ({ ...prev, [key]: { loading: false, error: e.message || String(e) } }));
    }
  };

  const toggleAmcRow = (amcName) => {
    if (expandedAmc === amcName) {
      setExpandedAmc(null);
    } else {
      setExpandedAmc(amcName);
      fetchAmcSchemes(amcName);
    }
  };

  const toggleSchemeRow = (amcName, schemeName) => {
    const key = `${amcName}|${schemeName}`;
    if (expandedScheme === key) {
      setExpandedScheme(null);
    } else {
      setExpandedScheme(key);
      fetchSchemeClients(amcName, schemeName);
    }
  };

  // Include name in the row key so multiple minors sharing one guardian PAN
  // retain distinct drill-down caches.
  const rowKeyForClient = (tab, c) => `${tab}:${c?.pan || c?.client_pan || c?.group_key || ''}:${(c?.name || '').toString().trim().toUpperCase()}`;

  const toggleDrillDown = useCallback(async (tab, client) => {
    const key = rowKeyForClient(tab, client);
    if (openDrillKey === key) { setOpenDrillKey(null); return; }
    setOpenDrillKey(key);
    // 2026-08-08: only skip refetch when a NON-EMPTY result is
    // already cached. Previously the check was `drillDown[key]?.folios`
    // which was truthy even for `[]`, so a transient empty response
    // (from an in-flight filter mismatch or a backend 502) got
    // cached forever → subsequent expands showed "No underlying
    // folios found" indefinitely.
    if ((drillDown[key]?.folios || []).length > 0) return;
    setDrillDown(prev => ({ ...prev, [key]: { loading: true, folios: [] } }));
    try {
      const pan = client?.pan || client?.client_pan || '';
      const gk = client?.group_key || '';
      // Different tabs expose the holder name under different keys
      // (`name` for summary tabs, `client_name` for underperformers /
      // reduced_portfolio). Without this fallback the drill-down's
      // `?name=` filter was being dropped, so it returned every name
      // variant on the same PAN — leading to "parent says 1 scheme /
      // ₹8K, drill-down says 2 schemes / ₹60K" mismatches.
      const nm = (client?.name || client?.client_name || '').toString().trim();
      const qs = new URLSearchParams({ tab });
      if (pan) qs.set('pan', pan);
      else if (gk) qs.set('group_key', gk);
      // Pass name so the backend can disambiguate minor accounts sharing
      // the guardian's PAN (reduced_portfolio / exited tabs).
      if (nm) qs.set('name', nm);
      // For Underperformers: mirror the user-selected filters on the parent
      // row. Without this the backend defaulted to min_holding_years=5 +
      // max_xirr_pct=14, so the drill-down surfaced lots the parent
      // (filtered at e.g. <10%) had excluded — leading to schemes like
      // Mirae @12.17% showing up under a 7.85% client row.
      if (tab === 'underperformers') {
        qs.set('min_holding_years', String(underMinYears));
        qs.set('max_xirr_pct', String(underMaxXirr));
      }
      // Outperformers mirror: pass the user's min XIRR floor through so the
      // drill-down only surfaces schemes that helped the client clear the
      // bar — preventing "client @18% / drill-down shows 9% scheme" mismatch.
      if (tab === 'outperformers') {
        qs.set('min_holding_years', String(outMinYears));
        qs.set('min_xirr_pct', String(outMinXirr));
      }
      // For Transmission tab drilldown: respect the currently active
      // sub-tab. Pending expansion should surface only folios that
      // still hold live units; Transmitted expansion should surface
      // only the fully-cleared folios. Without this both sub-tabs
      // shared one cache and Transmitted rows came up empty.
      if (tab === 'transmission') {
        qs.set('pending_only', transmissionSubTab === 'pending' ? 'true' : 'false');
      }
      const res = await fetch(`${API_URL}/api/dashboard/drilldown?${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDrillDown(prev => ({ ...prev, [key]: { loading: false, folios: data.folios || [] } }));
    } catch (err) {
      setDrillDown(prev => ({ ...prev, [key]: { loading: false, folios: [], error: err.message } }));
      toast.error('Failed to load drill-down');
    }
  }, [openDrillKey, drillDown, token, underMinYears, underMaxXirr, outMinYears, outMinXirr, transmissionSubTab]);

  // Wipe any cached underperformers drill-down results whenever the user
  // changes the min-years or max-XIRR filter so a fresh fetch is forced
  // (otherwise an already-open row would keep showing schemes from the
  // previous threshold — same root cause as the Mirae @12.17% bug).
  useEffect(() => {
    setDrillDown(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (!k.startsWith('underperformers:')) next[k] = v;
      });
      return next;
    });
  }, [underMinYears, underMaxXirr]);
  const eventSourceRef = useRef(null);

  // Sort reconciliation results
  const sortedReconciliationResults = useMemo(() => {
    if (!reconciliationResults.length) return [];
    
    return [...reconciliationResults].sort((a, b) => {
      let aVal = a[reconSortBy];
      let bVal = b[reconSortBy];
      
      // Handle numeric vs string comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        // For difference, sort by absolute value
        if (reconSortBy === 'difference') {
          aVal = Math.abs(aVal);
          bVal = Math.abs(bVal);
        }
        return reconSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
      if (reconSortOrder === 'asc') {
        return aVal.localeCompare(bVal);
      }
      return bVal.localeCompare(aVal);
    });
  }, [reconciliationResults, reconSortBy, reconSortOrder]);

  // Handle sort click for reconciliation table
  const handleReconSort = useCallback((field) => {
    if (reconSortBy === field) {
      setReconSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setReconSortBy(field);
      setReconSortOrder('desc');
    }
  }, [reconSortBy]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
      // Best-effort fetch of cache freshness — never blocks summary load.
      try {
        const cs = await fetch(`${API_URL}/api/dashboard/cache-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cs.ok) setCacheStatus(await cs.json());
      } catch (_e) { /* silent */ }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [token]);


  // Fetch Portfolio Mix (Equity / Debt / Hybrid bifurcation).
  const fetchPortfolioMix = useCallback(async () => {
    if (!token) return;
    setPortfolioMixLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/dashboard/portfolio-mix`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setPortfolioMix(await resp.json());
    } catch (e) {
      console.error("Error fetching portfolio mix:", e);
    } finally {
      setPortfolioMixLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPortfolioMix();
  }, [fetchPortfolioMix]);

  // Full Refresh = Transmission/TransferOut cache → Dashboard cache → reload summary.
  // Wires together the two server-side rebuilds that previously lived on Reset DB.
  const [fullRefreshing, setFullRefreshing] = useState(false);
  // `force=true` bypasses the anti-regression guard. Use when an intentional
  // drop is expected (e.g., Minor-Merge collapsing ~900 minors into guardian
  // PANs would otherwise breach the 2% client-drop cap and get rejected).
  const handleFullRefresh = useCallback(async (force = false) => {
    if (!token) return;
    setFullRefreshing(true);
    try {
      // Step 1 — supplementary caches (Transferout + Transmission).
      const r1 = await fetch(`${API_URL}/api/supplementary-caches/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r1.ok) {
        const j = await r1.json().catch(() => ({}));
        throw new Error(j.detail || "Transmission cache refresh failed");
      }
      const j1 = await r1.json();
      // Step 2 — main dashboard cache (optionally with force to bypass guard).
      const r2 = await fetch(
        `${API_URL}/api/dashboard/refresh-cache${force ? "?force=true" : ""}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!r2.ok) {
        const j = await r2.json().catch(() => ({}));
        throw new Error(j.detail || "Dashboard cache refresh failed");
      }
      // Supplementary refresh now runs ASYNC on the backend (avoids
      // Cloudflare 524 on production where it takes >100s). Show a
      // background-running message; the dashboard re-fetch below will
      // see the updated rows once they land (~30-60s later).
      toast.success(
        j1.status === "running"
          ? `Refresh queued${force ? " (FORCE — bypassing guard)" : ""}. Reload in ~60s to see updated numbers.`
          : `Refreshed${force ? " (FORCE)" : ""}: ${j1.transmission || 0} transmission · ${(j1.transferout_full || 0) + (j1.transferout_partial || 0)} transferout rows. Reloading dashboard…`,
        { duration: 6000 }
      );
      // Step 3 — re-fetch summary so the UI reflects new caches.
      await fetchDashboardData();
    } catch (e) {
      toast.error("Refresh failed", { description: String(e.message || e) });
    } finally {
      setFullRefreshing(false);
    }
  }, [token, fetchDashboardData]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch import status
  const fetchImportStatus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/dashboard/import-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setImportStatus(data);
      }
    } catch (error) {
      console.error("Error fetching import status:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchImportStatus();
  }, [fetchImportStatus]);

  // Fetch recent entries for verification
  const fetchRecentEntries = useCallback(async () => {
    if (!token) return;
    setLoadingRecentEntries(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/recent-entries?limit=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentEntries(data);
      }
    } catch (error) {
      console.error("Error fetching recent entries:", error);
    } finally {
      setLoadingRecentEntries(false);
    }
  }, [token]);

  // Fetch reconciliation status
  const fetchReconciliationStatus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/reconciliation/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReconciliationStatus(data);
      }
    } catch (error) {
      console.error("Error fetching reconciliation status:", error);
    }
  }, [token]);

  // Fetch stored mismatches from database
  const fetchStoredMismatches = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/reconciliation/units/all/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Load stored mismatches into the results state
        if (data.mismatches && data.mismatches.length > 0) {
          setReconciliationResults(data.mismatches);
        }
        // Update status with actual stored counts
        setReconciliationStatus(prev => ({
          ...prev,
          total_clients: data.total_clients || prev?.total_clients || 0,
          reconciled: data.clients_fully_reconciled || prev?.reconciled || 0,
          with_mismatches: data.clients_with_issues || prev?.with_mismatches || 0,
          stored_mismatches: data.total_mismatched || 0
        }));
      }
    } catch (error) {
      console.error("Error fetching stored mismatches:", error);
    }
  }, [token]);

  // Fetch status and stored mismatches when tab changes to Reconciliation
  useEffect(() => {
    if (activeTab === "reconciliation") {
      fetchReconciliationStatus();
      // Also load stored mismatches if we don't have any in memory
      if (reconciliationResults.length === 0) {
        fetchStoredMismatches();
      }
    }
  }, [activeTab, fetchReconciliationStatus, fetchStoredMismatches, reconciliationResults.length]);

  // Lazy-load Underperformers — re-fires whenever the user adjusts the
  // holding-period or XIRR-cap thresholds.  Defaults: holding ≥ 5 years AND
  // weighted XIRR < 14% (matches the "Provisional Sell" eligibility band).
  // 2026-08-08 — Lazy: only fetch when the tab is actually active.
  // The endpoint runs a per-lot aggregation over ~300 k active_units
  // + a TM cashflow XIRR pass; firing this on Dashboard mount (along
  // with every other analytics tab) was OOMing the backend under
  // real concurrent load.
  useEffect(() => {
    if (activeTab !== "underperformers") return;
    if (underperformers !== null || underperformersLoading) return;
    let cancelled = false;
    (async () => {
      setUnderperformersLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/underperformers?min_holding_years=${underMinYears}&max_xirr_pct=${underMaxXirr}&limit=10000`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setUnderperformers(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load underperformers", { description: String(e) });
      } finally {
        if (!cancelled) setUnderperformersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, underMinYears, underMaxXirr, token]);

  // Lazy-load Historical Data tab — folios with Transfer In but no WBR2A
  // historical data. Helps the user identify which folios to backfill from
  // CAMS WBR2A reports. Endpoint: GET /api/dashboard/historical-data.
  //
  // NOTE (2026-02-08): fetch on token-change (not only when the tab is
  // active) so the tab-badge "Historical Data (N)" reflects the real
  // count immediately — previously it stuck at 0 until the user clicked
  // in, which made the tab appear empty.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setHistoricalLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/historical-data?limit=10000`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setHistoricalData(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load historical data gaps", { description: String(e) });
      } finally {
        if (!cancelled) setHistoricalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Lazy-load Mismatch tab — surfaces every folio flagged by
  // /api/reconciliation/clients-with-unit-mismatch (which reads
  // transaction_master + aum_master via the self-healing endpoint).
  // 2026-07-07 user directive: dedicated Dashboard tab so the operator
  // sees discrepancies at a folio granularity, alongside Multi-Broker /
  // Historical Data.
  useEffect(() => {
    // 2026-08-08 — lazy: only fire when tab is active AND we don't
    // already have data. Previous guard `!== "mismatch" && data !== null`
    // fired the heavy 10k-row aggregation on Dashboard MOUNT (data was
    // still null), triggering 502s + OOM cascade alongside two other
    // similarly-broken effects (unpaid-brokerage 15 MB, profit-realignment
    // 16.8 MB). See lazy pattern used by /underperformers effect.
    if (activeTab !== "mismatch") return;
    if (mismatchData !== null || mismatchLoading) return;
    let cancelled = false;
    (async () => {
      setMismatchLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/reconciliation/clients-with-unit-mismatch?page_size=10000`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setMismatchData(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load mismatch folios", { description: String(e) });
      } finally {
        if (!cancelled) setMismatchLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, activeTab]);

  // Lazy-load Outperformers — mirror of Underperformers, flipped threshold.
  // Defaults: holding ≥ 5 years AND weighted XIRR ≥ 14% (the "long-term
  // winners" band, useful for spotlighting clients to upsell or reference).
  // 2026-08-08 — Lazy: only fetch when the tab is active (see
  // /underperformers effect for full rationale — mount-time
  // concurrent fan-out was OOMing the backend).
  useEffect(() => {
    if (activeTab !== "outperformers") return;
    if (outperformers !== null || outperformersLoading) return;
    let cancelled = false;
    (async () => {
      setOutperformersLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/outperformers?min_holding_years=${outMinYears}&min_xirr_pct=${outMinXirr}&limit=1000`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setOutperformers(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load outperformers", { description: String(e) });
      } finally {
        if (!cancelled) setOutperformersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, outMinYears, outMinXirr, token]);

  // Lazy-load ELSS by FY list (2026-08-08 — ONLY fires when user
  // opens the tab, not on Dashboard mount. The parent Dashboard
  // already fires several heavy aggregations in parallel; adding
  // this on mount was OOMing the backend under concurrent load.)
  useEffect(() => {
    if (activeTab !== "elss_pending") return;
    if (elssPending !== null || elssPendingLoading) return;
    let cancelled = false;
    (async () => {
      setElssPendingLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/elss-by-fy`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setElssPending(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load ELSS by FY", { description: String(e) });
      } finally {
        if (!cancelled) setElssPendingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, token]);

  // Lazy-load Profit Realignment list (added 2026-02-07)
  useEffect(() => {
    // 2026-08-08 — same lazy fix as /mismatch (previously fired on mount).
    // Response is ~16.8 MB — MUST NOT fetch until user opens the tab.
    if (activeTab !== "profit_realignment") return;
    if (profitRealignment !== null || profitRealignmentLoading) return;
    let cancelled = false;
    (async () => {
      setProfitRealignmentLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/profit-realignment?min_unrealized_gain=${profitMinGain}&limit=2000`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setProfitRealignment(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load profit realignment clients", { description: String(e) });
      } finally {
        if (!cancelled) setProfitRealignmentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, token, profitMinGain]);

  // Lazy-load Unpaid Brokerage list
  useEffect(() => {
    // 2026-08-08 — same lazy fix as /mismatch (previously fired on mount).
    // Response is ~15 MB (200 k rows) — MUST NOT fetch until user opens
    // the tab.
    if (activeTab !== "unpaid_brokerage") return;
    if (unpaidBrokerage !== null || unpaidBrokerageLoading) return;
    let cancelled = false;
    (async () => {
      setUnpaidBrokerageLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/unpaid-brokerage?limit=200000`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setUnpaidBrokerage(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load unpaid brokerage", { description: String(e) });
      } finally {
        if (!cancelled) setUnpaidBrokerageLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, token]);

  // Lazy-load Family AUM Leaderboard — fires when the tab is selected
  // or the min-members threshold changes.
  useEffect(() => {
    if (activeTab !== "family_aum") return;
    let cancelled = false;
    (async () => {
      setFamilyAumLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/family-aum?limit=5000&min_members=${familyMinMembers}`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setFamilyAum(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load family AUM", { description: String(e) });
      } finally {
        if (!cancelled) setFamilyAumLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, familyMinMembers, token]);

  // Start real-time reconciliation
  const startReconciliation = useCallback(async (mode = "pending") => {
    if (reconciliationRunning || !token) return;

    setReconciliationRunning(true);
    setReconciliationProgress({ processed: 0, total: 0, status: "Starting..." });
    
    // For "all" mode, clear results. For "pending" mode, load stored data first
    if (mode === "all") {
      setReconciliationResults([]);
      setSegregatedEntries([]);
      setTransferOutEntries([]);
    } else {
      // Load stored mismatches first so we show them during processing
      try {
        const response = await fetch(`${API_URL}/api/reconciliation/units/all/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.mismatches && data.mismatches.length > 0) {
            setReconciliationResults(data.mismatches);
          }
        }
      } catch (error) {
        console.error("Error loading stored mismatches:", error);
      }
    }

    // Use EventSource for SSE with mode parameter
    const eventSource = new EventSource(
      `${API_URL}/api/reconciliation/units/stream?token=${token}&mode=${mode}`
    );
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === "status") {
          // Initial status with counts
          setReconciliationStatus({
            total_clients: data.total_clients,
            reconciled: data.already_reconciled,
            with_mismatches: data.already_with_mismatches,
            pending: data.pending
          });
        } else if (data.type === "progress") {
          setReconciliationProgress({
            processed: data.processed,
            total: data.total,
            status: data.status,
            current_client: data.current_client
          });
        } else if (data.type === "mismatch") {
          // Regular mismatches (non-segregated) - check if already exists before adding
          setReconciliationResults(prev => {
            const exists = prev.some(m => 
              m.client_pan === data.mismatch.client_pan && 
              m.folio_no === data.mismatch.folio_no &&
              m.isin === data.mismatch.isin
            );
            if (exists) return prev;
            return [...prev, data.mismatch];
          });
        } else if (data.type === "segregated") {
          // Segregated portfolio entries - treat as non-financial/junk
          setSegregatedEntries(prev => [...prev, data.entry]);
        } else if (data.type === "transfer_out") {
          // Transfer out entries - holdings = 0 but purchases exist
          setTransferOutEntries(prev => [...prev, data.entry]);
        } else if (data.type === "complete") {
          setReconciliationProgress({
            processed: data.total_clients,
            total: data.total_clients,
            status: "Complete",
            summary: data.summary,
            message: data.message
          });
          setReconciliationRunning(false);
          eventSource.close();
          eventSourceRef.current = null;
          // Reload stored mismatches to get the complete picture
          fetchStoredMismatches();
          fetchReconciliationStatus();
          if (data.message) {
            toast.success(data.message);
          } else {
            toast.success(`Reconciliation complete! ${data.summary?.total_mismatched || 0} mismatches found.`);
          }
        } else if (data.type === "error") {
          toast.error(data.message);
          setReconciliationRunning(false);
          eventSource.close();
          eventSourceRef.current = null;
        }
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE Error:", error);
      // Only show error and stop if we're still supposed to be running
      if (eventSourceRef.current) {
        setReconciliationRunning(false);
        eventSource.close();
        eventSourceRef.current = null;
        fetchReconciliationStatus(); // Refresh status on error to show progress was saved
        toast.error("Connection lost. Progress has been saved. Click 'Resume' to continue.");
      }
    };
  }, [token, reconciliationRunning, fetchReconciliationStatus, fetchStoredMismatches]);

  // Stop reconciliation manually
  const stopReconciliation = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setReconciliationRunning(false);
      setReconciliationProgress(prev => prev ? { ...prev, status: "Stopped" } : null);
      toast.info("Reconciliation stopped.");
    }
  }, []);

  // Rerun reconciliation only for clients with existing mismatches
  const rerunMismatchesOnly = useCallback(async () => {
    if (!token) return;
    try {
      setReconciliationRunning(true);
      setReconciliationProgress({ processed: 0, total: 0, status: "Re-running mismatches only..." });
      
      const response = await fetch(`${API_URL}/api/reconciliation/rerun-mismatches`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Re-ran ${data.clients_processed || 0} clients. ${data.resolved || 0} resolved, ${data.still_mismatched || 0} still have issues.`);
        // Reload stored mismatches to reflect changes
        await fetchStoredMismatches();
        await fetchReconciliationStatus();
      } else {
        toast.error("Failed to rerun mismatches");
      }
    } catch (error) {
      console.error("Error rerunning mismatches:", error);
      toast.error("Error rerunning mismatches");
    } finally {
      setReconciliationRunning(false);
      setReconciliationProgress(null);
    }
  }, [token, fetchStoredMismatches, fetchReconciliationStatus]);

  // Cleanup ONLY on component unmount (not on tab change)
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Export reconciliation mismatches to Excel
  const exportMismatchesToExcel = useCallback(() => {
    if (reconciliationResults.length === 0) {
      toast.error("No mismatches to export");
      return;
    }

    // Prepare data for Excel
    const exportData = reconciliationResults.map(item => ({
      "Client Name": item.client_name,
      "PAN": item.client_pan,
      "Folio No": item.folio_no,
      "ISIN": item.isin,
      "Scheme Name": item.scheme_name,
      "Units Bought": item.units_bought,
      "Units Sold": item.units_sold,
      "Net (Transactions)": item.net_from_transactions,
      "Holdings (MFSD203)": item.balance_from_holdings,
      "Difference": item.difference,
      "Is Segregated": item.is_segregated ? "Yes" : "No",
      "Reason": item.reason || ""
    }));

    // Generate filename with timestamp & export via shared styled exporter.
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `Unit_Reconciliation_Mismatches_${timestamp}.xlsx`;
    exportStyledXlsx([{ name: "Unit Mismatches", rows: exportData }], filename);
    toast.success(`Exported ${reconciliationResults.length} mismatches to Excel`);
  }, [reconciliationResults]);

  // Sorting helper function (defined outside component render for stability)
  const sortData = useCallback((data, sortConfig) => {
    if (!sortConfig.column || !data) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.column];
      let bVal = b[sortConfig.column];
      
      // Handle null/undefined
      if (aVal == null) aVal = sortConfig.direction === 'asc' ? Infinity : -Infinity;
      if (bVal == null) bVal = sortConfig.direction === 'asc' ? Infinity : -Infinity;
      
      // Handle string comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      // Numeric comparison
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, []);

  // Extract data from dashboardData with defaults
  const summary = dashboardData?.summary || {};
  const holdingPatternBreakdown = dashboardData?.holding_pattern_breakdown || [];
  const aumBrackets = dashboardData?.aum_brackets || [];
  const amcBreakdown = dashboardData?.amc_breakdown || [];
  const xirrByHolding = dashboardData?.xirr_by_holding || [];
  const exitedClients = dashboardData?.exited_clients || { total_exited: 0, clients: [], exited: { clients: [] }, transfer_out: { clients: [] }, transmission: { clients: [] } };

  // Sorted data - must be called before any conditional returns
  const sortedXirrByHolding = useMemo(() => sortData(xirrByHolding, xirrSort), [xirrByHolding, xirrSort, sortData]);
  const sortedAmcBreakdown = useMemo(() => sortData(amcBreakdown, amcSort), [amcBreakdown, amcSort, sortData]);
  const sortedExitedClients = useMemo(() => sortData(exitedClients.clients, exitedSort), [exitedClients.clients, exitedSort, sortData]);

  // Filtered and sorted data for Exited tab
  const filteredExitedClients = useMemo(() => {
    let clients = exitedClients.exited?.clients || [];
    if (exitedSearch.trim()) {
      const search = exitedSearch.toLowerCase();
      clients = clients.filter(c => 
        (c.name || '').toLowerCase().includes(search) || 
        (c.pan || '').toLowerCase().includes(search)
      );
    }
    return sortData(clients, exitedSort);
  }, [exitedClients.exited?.clients, exitedSearch, exitedSort, sortData]);

  // ── Client-side CSV exporter ─────────────────────────────────────
  // Shared by tabs that don't have a dedicated server-side export
  // endpoint (Reduced Portfolio / Underperformers / Outperformers).
  // `columns` is an array of {key, label, fmt?} — `fmt` receives the
  // raw row value and returns the CSV cell. Strings with commas,
  // quotes or newlines are auto-escaped.
  const downloadCsv = useCallback((rows, columns, filename) => {
    if (!rows || rows.length === 0) {
      toast.info('No rows to export');
      return;
    }
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = columns.map(c => escape(c.label)).join(',');
    const body = rows.map(r => columns.map(c => {
      const raw = r[c.key];
      return escape(c.fmt ? c.fmt(raw, r) : raw);
    }).join(',')).join('\r\n');
    const blob = new Blob(["\uFEFF" + header + '\r\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
    toast.success(`Exported ${rows.length} rows`, { description: filename });
  }, []);


  // Filtered and sorted data for Transfer Out tab
  const filteredTransferOutClients = useMemo(() => {
    let clients = exitedClients.transfer_out?.clients || [];
    if (transferOutSearch.trim()) {
      const search = transferOutSearch.toLowerCase();
      clients = clients.filter(c => 
        (c.name || '').toLowerCase().includes(search) || 
        (c.pan || '').toLowerCase().includes(search)
      );
    }
    return sortData(clients, transferOutSort);
  }, [exitedClients.transfer_out?.clients, transferOutSearch, transferOutSort, sortData]);

  // Filtered and sorted data for Transmission tab
  const filteredTransmissionClients = useMemo(() => {
    let clients = exitedClients.transmission?.clients || [];
    if (transmissionSearch.trim()) {
      const search = transmissionSearch.toLowerCase();
      clients = clients.filter(c => 
        (c.name || '').toLowerCase().includes(search) || 
        (c.pan || '').toLowerCase().includes(search)
      );
    }
    return sortData(clients, transmissionSort);
  }, [exitedClients.transmission?.clients, transmissionSearch, transmissionSort, sortData]);

  // Filtered + sorted for new Transfer Out tab
  const filteredNewTransferOut = useMemo(() => {
    let clients = dashboardData?.transfer_out?.clients || [];
    if (transferOutSearch) {
      const s = transferOutSearch.toLowerCase();
      clients = clients.filter(c => (c.name||'').toLowerCase().includes(s) || (c.pan||'').toLowerCase().includes(s));
    }
    return sortData(clients, newTransferOutSort);
  }, [dashboardData?.transfer_out?.clients, transferOutSearch, newTransferOutSort, sortData]);

  // Filtered + sorted for new Transmission tab
  // 2026-08-08 — split into Transmitted / Pending sub-tabs (state is
  // declared alongside `drillDown` above so `toggleDrillDown` can
  // reference it without a TDZ error).
  const filteredNewTransmission = useMemo(() => {
    let clients = dashboardData?.transmission_death?.clients || [];
    if (transmissionSearch) {
      const s = transmissionSearch.toLowerCase();
      clients = clients.filter(c => (c.name||'').toLowerCase().includes(s) || (c.pan||'').toLowerCase().includes(s));
    }
    // Sub-tab split: Pending has units still in the folio; Transmitted is
    // fully-nominee-transferred (units == 0).
    clients = clients.filter(c => transmissionSubTab === 'pending' ? c.is_pending : !c.is_pending);
    return sortData(clients, newTransmissionSort);
  }, [dashboardData?.transmission_death?.clients, transmissionSearch, newTransmissionSort, sortData, transmissionSubTab]);

  // Filtered + sorted for Multi Broker tab
  const filteredMultiBroker = useMemo(() => {
    let clients = dashboardData?.multi_broker?.clients || [];
    if (multiBrokerSearch) {
      const s = multiBrokerSearch.toLowerCase();
      clients = clients.filter(c => (c.name||'').toLowerCase().includes(s) || (c.pan||'').toLowerCase().includes(s) || (c.folio_no||'').includes(s) || (c.scheme_name||'').toLowerCase().includes(s));
    }
    return sortData(clients, multiBrokerSort);
  }, [dashboardData?.multi_broker?.clients, multiBrokerSearch, multiBrokerSort, sortData]);

  // Toggle sort handler
  const handleSort = (column, currentSort, setSort) => {
    setSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ column, sortConfig }) => {
    if (sortConfig.column !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-teal-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-teal-600" />;
  };

  // Reusable drill-down sub-row shown under an expanded PAN row.
  const DrillDownSubRow = ({ tab, client, colSpan, columns }) => {
    const key = rowKeyForClient(tab, client);
    const state = drillDown[key] || {};
    return (
      <TableRow className="bg-slate-50" data-testid={`drilldown-row-${tab}`}>
        <TableCell colSpan={colSpan} className="p-0">
          <div className="px-6 py-4 border-l-4 border-teal-500">
            <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
              Folio / Scheme breakdown for {toTitleCase(client.name || client.client_name || '')}
            </div>
            {state.loading && (
              <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            )}
            {!state.loading && (state.folios || []).length === 0 && (
              <div className="text-sm text-gray-400 italic py-2">No underlying folios found</div>
            )}
            {!state.loading && (state.folios || []).length > 0 && (
              <div className="rounded border overflow-x-auto bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="h-8 text-xs font-semibold">Folio No</TableHead>
                      <TableHead className="h-8 text-xs font-semibold">Scheme</TableHead>
                      <TableHead className="h-8 text-xs font-semibold">Src</TableHead>
                      {columns.map(c => (
                        <TableHead key={c.key} className={`h-8 text-xs font-semibold ${c.align==='right' ? 'text-right' : ''}`}>{c.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {state.folios.map((f, fi) => (
                      <TableRow key={`${f.folio_no}-${f.product_code}-${fi}`} className="hover:bg-gray-50">
                        <TableCell className="py-1.5 font-mono text-xs">{f.folio_no}</TableCell>
                        <TableCell className="py-1.5 text-xs max-w-[320px]">{f.scheme_name}</TableCell>
                        <TableCell className="py-1.5 text-xs text-gray-500">{f.source}</TableCell>
                        {columns.map(c => (
                          <TableCell key={c.key} className={`py-1.5 text-xs font-mono ${c.align==='right' ? 'text-right' : ''} ${c.colorFn ? c.colorFn(f[c.key]) : ''}`}>
                            {c.render ? c.render(f[c.key], f) : (f[c.key] ?? '—')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const DrillCaret = ({ tab, client }) => {
    const key = rowKeyForClient(tab, client);
    return openDrillKey === key
      ? <ChevronDown className="h-4 w-4 text-teal-600 inline-block" />
      : <ChevronRight className="h-4 w-4 text-gray-400 inline-block" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        <span className="ml-3 text-gray-500">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      {/* Welcome tour for brand-new tenants (?welcome=1 after wizard) */}
      <WelcomeTour />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <PieChartIcon className="h-6 w-6 text-teal-600" />
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Client portfolio overview and analytics</span>
            {cacheStatus?.cached_at && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-[11px] text-gray-600"
                data-testid="dashboard-last-updated"
                title={`Full dashboard cache built in ${cacheStatus.calculation_time_seconds?.toFixed?.(1) || '—'}s · ${cacheStatus.total_clients?.toLocaleString?.('en-IN') || '—'} clients`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Last updated&nbsp;
                <span className="font-medium text-gray-800">
                  {(() => {
                    try {
                      const d = new Date(cacheStatus.cached_at);
                      // Show as dd-MMM-yyyy HH:mm IST
                      return d.toLocaleString("en-IN", {
                        day: "2-digit", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit", hour12: false,
                        timeZone: "Asia/Kolkata",
                      }) + " IST";
                    } catch {
                      return cacheStatus.cached_at;
                    }
                  })()}
                </span>
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Reconciliation status badge - visible from any tab */}
          {reconciliationRunning && (
            <div 
              className="flex items-center gap-2 px-3 py-2 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
              onClick={() => setActiveTab("reconciliation")}
              title="Click to view reconciliation progress"
            >
              <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
              <span className="text-sm text-orange-700">
                Reconciling: {reconciliationProgress?.processed || 0}/{reconciliationProgress?.total || 0}
              </span>
            </div>
          )}
          <Button
            onClick={() => handleFullRefresh(false)}
            disabled={loading || fullRefreshing}
            variant="outline"
            data-testid="dashboard-refresh-button"
            title="Rebuild Transmission cache + Dashboard cache + reload — ~30s"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${fullRefreshing || loading ? "animate-spin" : ""}`} />
            {fullRefreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            onClick={() => {
              if (window.confirm(
                "FORCE refresh bypasses the anti-regression guard (allows >2% client drop / >3% AUM drop).\n\n" +
                "Use this ONLY when you expect a legitimate drop (e.g. Minor-Merge, quarter-end bulk exits).\n\n" +
                "Proceed?"
              )) {
                handleFullRefresh(true);
              }
            }}
            disabled={loading || fullRefreshing}
            variant="outline"
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
            data-testid="dashboard-force-refresh-button"
            title="Bypass anti-regression guard — use when intentional drop expected"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Force Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards - Row 1: Client Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {!hiddenCards.total_clients && (
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-teal-600 font-medium">Total Clients</p>
                <p className="text-3xl font-bold text-teal-700" data-testid="dashboard-total-clients">{summary.total_clients?.toLocaleString() || 0}</p>
                <p className="text-xs text-teal-500 mt-1" data-testid="dashboard-total-clients-breakdown">
                  With PAN: <span className="font-medium text-teal-700">{(summary.clients_with_pan ?? 0).toLocaleString()}</span>
                  {" | "}
                  Name-matched: <span className="font-medium text-teal-700">{(summary.clients_name_matched ?? 0).toLocaleString()}</span>
                  {summary.clients_zero_aum > 0 && (
                    <>
                      {" | "}
                      Excluded: <span className="font-medium text-teal-700">{summary.clients_zero_aum.toLocaleString()}</span>
                    </>
                  )}
                </p>
              </div>
              <Users className="h-10 w-10 text-teal-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.total_folios && (
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Total Folios</p>
                <p className="text-3xl font-bold text-purple-700">{summary.total_folios?.toLocaleString() || 0}</p>
                <p className="text-xs text-purple-500 mt-1">Active folio accounts</p>
              </div>
              <Briefcase className="h-10 w-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.avg_portfolio && (
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Avg. Portfolio</p>
                <p className="text-3xl font-bold text-orange-700">{formatCurrency(summary.avg_portfolio)}</p>
                <p className="text-xs text-orange-500 mt-1">Per client average</p>
              </div>
              <TrendingUp className="h-10 w-10 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.overall_xirr && (
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Overall XIRR</p>
                <p className={`text-3xl font-bold ${(summary.overall_xirr || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(summary.overall_xirr || 0) >= 0 ? '+' : ''}{(summary.overall_xirr || 0).toFixed(2)}%
                </p>
                <p className="text-xs text-slate-500 mt-1">Annualized return</p>
              </div>
              <TrendingUp className={`h-10 w-10 ${(summary.overall_xirr || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} />
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Summary Cards - Row 2: AUM Breakdown with Cost/Market/Profit */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {!hiddenCards.cams && (
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">CAMS</p>
                <p className="text-3xl font-bold text-blue-700">{formatCurrency(summary.cams_market || summary.cams_aum || 0)}</p>
                <p className="text-xs text-blue-500 mt-1">
                  Cost: {formatCurrency(summary.cams_cost || 0)} | Profit: <span className={`font-medium ${(summary.cams_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{(summary.cams_profit || 0) >= 0 ? '+' : ''}{formatCurrency(summary.cams_profit || 0)}</span>
                </p>
              </div>
              <IndianRupee className="h-10 w-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.karvy && (
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">KARVY</p>
                <p className="text-3xl font-bold text-purple-700">{formatCurrency(summary.karvy_market || summary.karvy_aum || 0)}</p>
                <p className="text-xs text-purple-500 mt-1">
                  Cost: {formatCurrency(summary.karvy_cost || 0)} | Profit: <span className={`font-medium ${(summary.karvy_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{(summary.karvy_profit || 0) >= 0 ? '+' : ''}{formatCurrency(summary.karvy_profit || 0)}</span>
                </p>
              </div>
              <IndianRupee className="h-10 w-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.combined_aum && (
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Combined AUM</p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(summary.total_aum)}</p>
                <p className="text-xs text-green-500 mt-1">CAMS + Karvy total</p>
              </div>
              <IndianRupee className="h-10 w-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
        )}

        {!hiddenCards.total_profit_loss && (
        <Card className={`bg-gradient-to-br ${(summary.total_profit_loss || 0) >= 0 ? 'from-emerald-50 to-emerald-100 border-emerald-200' : 'from-red-50 to-red-100 border-red-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${(summary.total_profit_loss || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  Total {(summary.total_profit_loss || 0) >= 0 ? 'Profit' : 'Loss'}
                </p>
                <p className={`text-3xl font-bold ${(summary.total_profit_loss || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {(summary.total_profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(summary.total_profit_loss)}
                </p>
                <p className={`text-xs mt-1 ${(summary.total_profit_loss || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {summary.total_cost_value > 0 ? `${((summary.total_profit_loss || 0) / summary.total_cost_value * 100).toFixed(1)}% gain` : 'Total P&L'}
                </p>
              </div>
              <TrendingUp className={`h-10 w-10 ${(summary.total_profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Tabs for Analytics */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {isMobile ? (
          <MobileTabDropdown
            activeKey={activeTab}
            onChange={setActiveTab}
            tabs={[
              { key: "overview", label: "Overview", icon: PieChartIcon },
              { key: "exited", label: "Exited", icon: TrendingUp, count: exitedClients.exited?.count || 0 },
              { key: "multi_broker", label: "Multi Broker", icon: GitBranch, count: dashboardData?.multi_broker?.count || 0 },
              { key: "reduced_portfolio", label: "Reduced Portfolio", icon: TrendingUp, count: dashboardData?.reduced_portfolio?.count || 0 },
              { key: "underperformers", label: "Underperformers", icon: TrendingUp },
              { key: "outperformers", label: "Outperformers", icon: TrendingUp },
              { key: "elss_pending", label: "ELSS by FY", icon: AlertCircle },
              { key: "profit_realignment", label: "Profit Realignment", icon: Target },
              { key: "unpaid_brokerage", label: "Unpaid Brokerage", icon: DollarSign },
              { key: "historical_data", label: "Historical Data", icon: FileWarning, count: historicalData?.count || 0 },
              ...(isSubBroker ? [] : [{ key: "mismatch", label: "Mismatch", icon: AlertTriangle, count: mismatchData?.total || 0 }]),
            ]}
          />
        ) : (
        <TabsList className="bg-gray-100">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">
            <PieChartIcon className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="exited" className="data-[state=active]:bg-orange-100">
            <TrendingUp className="h-4 w-4 mr-2" />
            Exited ({exitedClients.exited?.count || 0})
          </TabsTrigger>
          <TabsTrigger value="multi_broker" className="data-[state=active]:bg-gray-200" data-testid="multi-broker-tab">
            <GitBranch className="h-4 w-4 mr-2" />
            Multi Broker ({dashboardData?.multi_broker?.count || 0})
          </TabsTrigger>
          <TabsTrigger value="reduced_portfolio" className="data-[state=active]:bg-amber-100" data-testid="reduced-portfolio-tab">
            <TrendingUp className="h-4 w-4 mr-2" />
            Reduced Portfolio ({dashboardData?.reduced_portfolio?.count || 0})
          </TabsTrigger>
          <TabsTrigger value="underperformers" className="data-[state=active]:bg-red-100" data-testid="underperformers-tab">
            <TrendingUp className="h-4 w-4 mr-2" />
            Underperformers{underperformers ? ` (${underperformers.count || 0})` : ""}
          </TabsTrigger>
          <TabsTrigger value="outperformers" className="data-[state=active]:bg-emerald-100" data-testid="outperformers-tab">
            <TrendingUp className="h-4 w-4 mr-2" />
            Outperformers{outperformers ? ` (${outperformers.count || 0})` : ""}
          </TabsTrigger>
          <TabsTrigger value="elss_pending" className="data-[state=active]:bg-emerald-100" data-testid="elss-pending-tab">
            <AlertCircle className="h-4 w-4 mr-2" />
            ELSS by FY{elssPending ? ` (${elssPending.total_fy_count || 0})` : ""}
          </TabsTrigger>
          <TabsTrigger value="profit_realignment" className="data-[state=active]:bg-emerald-100" data-testid="profit-realignment-tab">
            <Target className="h-4 w-4 mr-2" />
            Profit Realignment{profitRealignment ? ` (${profitRealignment.total_clients || 0})` : ""}
          </TabsTrigger>
          <TabsTrigger value="unpaid_brokerage" className="data-[state=active]:bg-rose-100" data-testid="unpaid-brokerage-tab">
            <DollarSign className="h-4 w-4 mr-2" />
            Unpaid Brokerage{unpaidBrokerage ? ` (${unpaidBrokerage.total_pans || 0})` : ""}
          </TabsTrigger>
          {!isSubBroker && (
          <TabsTrigger value="historical_data" className="data-[state=active]:bg-sky-100" data-testid="historical-data-tab">
            <FileWarning className="h-4 w-4 mr-2" />
            Historical Data ({historicalData?.count || 0})
          </TabsTrigger>
          )}
          {!isSubBroker && (
          <TabsTrigger value="mismatch" className="data-[state=active]:bg-red-100" data-testid="mismatch-tab">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Mismatch ({mismatchData?.total || 0})
          </TabsTrigger>
          )}
          <TabsTrigger value="family_aum" className="hidden" data-testid="family-aum-tab">
            <Crown className="h-4 w-4 mr-2" />
            Family AUM
          </TabsTrigger>
        </TabsList>
        )}

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          
          {/* XIRR BASIS HOLDING PERIOD - Same format as Portfolio page */}
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-teal-600" />
                  XIRR BASIS HOLDING PERIOD
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/dashboard/xirr-holdings-excel`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.detail || 'Download failed');
                      }
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `XIRR_Holdings_${new Date().toISOString().slice(0,10)}.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      a.remove();
                      toast.success('Excel file downloaded successfully');
                    } catch (error) {
                      toast.error(error.message || 'Failed to download Excel');
                    }
                  }}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Excel
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('period', xirrSort, setXirrSort)}
                      >
                        <div className="flex items-center">
                          Holding Period
                          <SortIcon column="period" sortConfig={xirrSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('holdings_count', xirrSort, setXirrSort)}
                      >
                        <div className="flex items-center justify-end">
                          No. of Holdings
                          <SortIcon column="holdings_count" sortConfig={xirrSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('cost_value', xirrSort, setXirrSort)}
                      >
                        <div className="flex items-center justify-end">
                          Cost Value (INR)
                          <SortIcon column="cost_value" sortConfig={xirrSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('market_value', xirrSort, setXirrSort)}
                      >
                        <div className="flex items-center justify-end">
                          Market Value (INR)
                          <SortIcon column="market_value" sortConfig={xirrSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('gain_loss', xirrSort, setXirrSort)}
                      >
                        <div className="flex items-center justify-end">
                          Gain/Loss (INR)
                          <SortIcon column="gain_loss" sortConfig={xirrSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('gain_loss_pct', xirrSort, setXirrSort)}
                      >
                        <div className="flex items-center justify-end">
                          Gain/Loss %
                          <SortIcon column="gain_loss_pct" sortConfig={xirrSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('avg_xirr', xirrSort, setXirrSort)}
                      >
                        <div className="flex items-center justify-end">
                          Avg XIRR %
                          <SortIcon column="avg_xirr" sortConfig={xirrSort} />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedXirrByHolding.map((item, idx) => (
                      <TableRow key={idx} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{item.period}</TableCell>
                        <TableCell className="text-right font-mono">{item.holdings_count?.toLocaleString() || 0}</TableCell>
                        <TableCell className="text-right font-mono">₹{(item.cost_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-right font-mono">₹{(item.market_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className={`text-right font-mono ${(item.gain_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(item.gain_loss || 0) >= 0 ? '+' : ''}₹{Math.abs(item.gain_loss || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${(item.gain_loss_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(item.gain_loss_pct || 0) >= 0 ? '+' : ''}{(item.gain_loss_pct || 0).toFixed(2)}%
                        </TableCell>
                        <TableCell className={`text-right font-mono ${(item.avg_xirr || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {/* Clamp at ±999.99% — JS toFixed(2) on values ≥ 1e21 returns
                              scientific notation ("4.708e+21%"). For very short holdings
                              the (mkt/cost)^(1/0.5)-1 formula can explode when a lot has
                              a near-zero cost (rounding / IDCW precision). The user wants
                              max 2 decimals, so we clamp the display to a sane range. */}
                          {(() => {
                            const raw = Number(item.avg_xirr || 0);
                            const safe = !Number.isFinite(raw) ? 0 : Math.max(-999.99, Math.min(999.99, raw));
                            return `${safe >= 0 ? '+' : ''}${safe.toFixed(2)}%`;
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Grand Total Row */}
                    <TableRow className="bg-gray-100 font-semibold border-t-2">
                      <TableCell className="font-bold">GRAND TOTAL</TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {xirrByHolding.reduce((sum, item) => sum + (item.holdings_count || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        ₹{xirrByHolding.reduce((sum, item) => sum + (item.cost_value || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        ₹{xirrByHolding.reduce((sum, item) => sum + (item.market_value || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-bold ${
                        xirrByHolding.reduce((sum, item) => sum + (item.gain_loss || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {xirrByHolding.reduce((sum, item) => sum + (item.gain_loss || 0), 0) >= 0 ? '+' : ''}
                        ₹{Math.abs(xirrByHolding.reduce((sum, item) => sum + (item.gain_loss || 0), 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-bold ${
                        (() => {
                          const totalCost = xirrByHolding.reduce((sum, item) => sum + (item.cost_value || 0), 0);
                          const totalGain = xirrByHolding.reduce((sum, item) => sum + (item.gain_loss || 0), 0);
                          return totalCost > 0 ? (totalGain / totalCost * 100) >= 0 : true;
                        })() ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(() => {
                          const totalCost = xirrByHolding.reduce((sum, item) => sum + (item.cost_value || 0), 0);
                          const totalGain = xirrByHolding.reduce((sum, item) => sum + (item.gain_loss || 0), 0);
                          const pct = totalCost > 0 ? (totalGain / totalCost * 100) : 0;
                          return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">-</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* PORTFOLIO MIX — Equity / Debt / Hybrid bifurcation (iteration 37) */}
          <Card className="border border-slate-200 shadow-sm" data-testid="portfolio-mix-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-slate-900">Portfolio Mix — Equity / Debt / Hybrid</CardTitle>
                  <CardDescription className="text-xs text-slate-500">
                    Category bifurcation across all clients in your scope. Auto-classified via SEBI scheme_category.
                  </CardDescription>
                </div>
                {portfolioMixLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
            </CardHeader>
            <CardContent>
              {!portfolioMix && !portfolioMixLoading && (
                <div className="text-sm text-slate-400 italic py-4">Loading portfolio mix…</div>
              )}
              {portfolioMix && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    const colors = {
                      Equity: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500' },
                      Debt:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   bar: 'bg-amber-500' },
                      Hybrid: { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  bar: 'bg-indigo-500' },
                    };
                    return ['Equity', 'Debt', 'Hybrid'].map((cat) => {
                      const b = portfolioMix.overall?.[cat] || {};
                      const c = colors[cat];
                      return (
                        <div key={cat} className={`${c.bg} ${c.border} border rounded-lg p-4`} data-testid={`portfolio-mix-${cat.toLowerCase()}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className={`text-sm font-semibold ${c.text}`}>{cat}</div>
                            <div className={`text-xs font-mono px-2 py-0.5 rounded-full ${c.bg} ${c.text} border ${c.border}`}>
                              {(b.share_pct ?? 0).toFixed(1)}%
                            </div>
                          </div>
                          <div className="space-y-1.5 text-[13px]">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Market</span>
                              <span className="font-mono font-semibold text-slate-900">
                                ₹{((b.market_value ?? 0) / 1e7).toFixed(2)} Cr
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Cost</span>
                              <span className="font-mono text-slate-700">
                                ₹{((b.cost_value ?? 0) / 1e7).toFixed(2)} Cr
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Gain / Loss</span>
                              <span className={`font-mono font-semibold ${(b.profit_loss ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                {(b.profit_loss ?? 0) >= 0 ? '+' : ''}₹{Math.abs((b.profit_loss ?? 0) / 1e7).toFixed(2)} Cr ({(b.gain_pct ?? 0).toFixed(1)}%)
                              </span>
                            </div>
                            <div className="flex justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-200/50">
                              <span>{(b.holdings_count ?? 0).toLocaleString()} holdings</span>
                              <span>{(b.clients_count ?? 0).toLocaleString()} clients</span>
                            </div>
                          </div>
                          <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`${c.bar} h-full`} style={{ width: `${b.share_pct ?? 0}%` }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
              {portfolioMix && (
                <div className="text-[11px] text-slate-400 mt-3 text-right font-mono">
                  Grand market: ₹{((portfolioMix.grand_market ?? 0) / 1e7).toFixed(2)} Cr
                </div>
              )}
            </CardContent>
          </Card>

          {/* AMC-WISE BREAKDOWN - Simplified with XIRR */}
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-teal-600" />
                  AMC-WISE BREAKDOWN
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/dashboard/amc-holdings-excel`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.detail || 'Download failed');
                      }
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `AMC_Holdings_${new Date().toISOString().slice(0,10)}.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      a.remove();
                      toast.success('Excel file downloaded successfully');
                    } catch (error) {
                      toast.error(error.message || 'Failed to download Excel');
                    }
                  }}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Excel
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('amc_name', amcSort, setAmcSort)}
                      >
                        <div className="flex items-center">
                          Mutual Fund (AMC)
                          <SortIcon column="amc_name" sortConfig={amcSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('cost_value', amcSort, setAmcSort)}
                      >
                        <div className="flex items-center justify-end">
                          Cost Value (INR)
                          <SortIcon column="cost_value" sortConfig={amcSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('market_value', amcSort, setAmcSort)}
                      >
                        <div className="flex items-center justify-end">
                          Market Value (INR)
                          <SortIcon column="market_value" sortConfig={amcSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('profit_loss', amcSort, setAmcSort)}
                      >
                        <div className="flex items-center justify-end">
                          Profit/Loss (INR)
                          <SortIcon column="profit_loss" sortConfig={amcSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('xirr_pct', amcSort, setAmcSort)}
                      >
                        <div className="flex items-center justify-end">
                          XIRR %
                          <SortIcon column="xirr_pct" sortConfig={amcSort} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-center" data-testid="amc-mix-col-header">
                        <div className="text-xs">Mix (E / D / H)</div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedAmcBreakdown.map((amc, idx) => {
                      // Prefer backend-computed XIRR (pooled cost-weighted actual years).
                      // Fallback to a simple 3-year annualised proxy ONLY if backend hasn't supplied xirr_pct.
                      const xirrPct = (amc.xirr_pct !== undefined && amc.xirr_pct !== null)
                        ? Number(amc.xirr_pct)
                        : (amc.cost_value > 0
                            ? ((Math.pow(amc.market_value / amc.cost_value, 1/3) - 1) * 100)
                            : 0);
                      const xirrSafe = !Number.isFinite(xirrPct) ? 0 : Math.max(-999.99, Math.min(999.99, xirrPct));
                      const isExpanded = expandedAmc === amc.amc_name;
                      const schemeState = amcSchemes[amc.amc_name] || {};
                      // Look up this AMC's Equity/Debt/Hybrid mix (iteration 37).
                      // The dashboard's amc_breakdown uses canonicalised
                      // "* Mutual Fund" names while portfolio-mix's by_amc uses
                      // scheme_master.amc_name (which reads like "* Asset
                      // Management Company Limited"). Normalize both by
                      // stripping common suffixes/prefixes and comparing.
                      const _normAmc = (s) => (s || "")
                        .toLowerCase()
                        .replace(/\bmutual fund\b/g, "")
                        .replace(/\basset management\b/g, "")
                        .replace(/\bcompany\b/g, "")
                        .replace(/\bcompanies\b/g, "")
                        .replace(/\blimited\b/g, "")
                        .replace(/\bindia\b/g, "")
                        .replace(/\bltd\.?\b/g, "")
                        .replace(/\bco\.?\b/g, "")
                        .replace(/\bpvt\.?\b/g, "")
                        .replace(/[.,()]/g, "")
                        .replace(/\s+/g, " ")
                        .trim();
                      const _mixByAmc = (portfolioMix?.by_amc || []);
                      const _key = _normAmc(amc.amc_name);
                      const mixRow = _mixByAmc.find(m => {
                        const mk = _normAmc(m.amc);
                        return mk && _key && (mk === _key || mk.startsWith(_key) || _key.startsWith(mk));
                      });
                      const eShare = mixRow?.Equity?.share_pct ?? 0;
                      const dShare = mixRow?.Debt?.share_pct ?? 0;
                      const hShare = mixRow?.Hybrid?.share_pct ?? 0;
                      return (
                        <React.Fragment key={`amc-${idx}`}>
                          <TableRow
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleAmcRow(amc.amc_name)}
                            data-testid={`amc-row-${idx}`}
                          >
                            <TableCell className="font-medium max-w-[300px]">
                              <div className="flex items-center gap-1.5">
                                {isExpanded
                                  ? <ChevronDown className="h-4 w-4 text-teal-600 shrink-0" />
                                  : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                                <span className="truncate" title={amc.amc_name}>{amc.amc_name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">₹{(amc.cost_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono">₹{(amc.market_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className={`text-right font-mono ${(amc.profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(amc.profit_loss || 0) >= 0 ? '+' : ''}₹{Math.abs(amc.profit_loss || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className={`text-right font-mono ${xirrSafe >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {xirrSafe >= 0 ? '+' : ''}{xirrSafe.toFixed(2)}%
                            </TableCell>
                            <TableCell className="text-center" data-testid={`amc-mix-${idx}`}>
                              {mixRow ? (
                                <div className="inline-flex flex-col items-center gap-1 min-w-[110px]">
                                  <div className="flex w-24 h-2 rounded-full overflow-hidden bg-slate-100" title={`Equity ${eShare.toFixed(0)}% · Debt ${dShare.toFixed(0)}% · Hybrid ${hShare.toFixed(0)}%`}>
                                    <div className="bg-emerald-500 h-full" style={{ width: `${eShare}%` }} />
                                    <div className="bg-amber-500 h-full" style={{ width: `${dShare}%` }} />
                                    <div className="bg-indigo-500 h-full" style={{ width: `${hShare}%` }} />
                                  </div>
                                  <div className="text-[10px] font-mono text-slate-500">
                                    <span className="text-emerald-700">{eShare.toFixed(0)}</span>
                                    /
                                    <span className="text-amber-700">{dShare.toFixed(0)}</span>
                                    /
                                    <span className="text-indigo-700">{hShare.toFixed(0)}</span>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-300">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow className="bg-slate-50" data-testid={`amc-drilldown-${idx}`}>
                              <TableCell colSpan={6} className="p-0">
                                <div className="px-6 py-4 border-l-4 border-teal-500">
                                  <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                    Schemes under {amc.amc_name}
                                  </div>
                                  {schemeState.loading && (
                                    <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
                                      <Loader2 className="h-4 w-4 animate-spin" /> Loading schemes…
                                    </div>
                                  )}
                                  {!schemeState.loading && schemeState.error && (
                                    <div className="text-sm text-red-500 py-2">Failed to load: {schemeState.error}</div>
                                  )}
                                  {!schemeState.loading && !schemeState.error && (schemeState.data || []).length === 0 && (
                                    <div className="text-sm text-gray-400 italic py-2">No schemes under this AMC in scope</div>
                                  )}
                                  {!schemeState.loading && (schemeState.data || []).length > 0 && (
                                    <div className="rounded border overflow-x-auto bg-white">
                                      <Table>
                                        <TableHeader>
                                          <TableRow className="bg-gray-100">
                                            <TableHead className="h-8 text-xs font-semibold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('scheme_name', amcSchemeSort, setAmcSchemeSort)}>
                                              <div className="flex items-center">Scheme<SortIcon column="scheme_name" sortConfig={amcSchemeSort} /></div>
                                            </TableHead>
                                            <TableHead className="h-8 text-xs font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('holdings_count', amcSchemeSort, setAmcSchemeSort)}>
                                              <div className="flex items-center justify-end">Holdings<SortIcon column="holdings_count" sortConfig={amcSchemeSort} /></div>
                                            </TableHead>
                                            <TableHead className="h-8 text-xs font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('units', amcSchemeSort, setAmcSchemeSort)}>
                                              <div className="flex items-center justify-end">Units<SortIcon column="units" sortConfig={amcSchemeSort} /></div>
                                            </TableHead>
                                            <TableHead className="h-8 text-xs font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('cost_value', amcSchemeSort, setAmcSchemeSort)}>
                                              <div className="flex items-center justify-end">Cost (₹)<SortIcon column="cost_value" sortConfig={amcSchemeSort} /></div>
                                            </TableHead>
                                            <TableHead className="h-8 text-xs font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('market_value', amcSchemeSort, setAmcSchemeSort)}>
                                              <div className="flex items-center justify-end">Market (₹)<SortIcon column="market_value" sortConfig={amcSchemeSort} /></div>
                                            </TableHead>
                                            <TableHead className="h-8 text-xs font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('profit_loss', amcSchemeSort, setAmcSchemeSort)}>
                                              <div className="flex items-center justify-end">P/L (₹)<SortIcon column="profit_loss" sortConfig={amcSchemeSort} /></div>
                                            </TableHead>
                                            <TableHead className="h-8 text-xs font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('xirr_pct', amcSchemeSort, setAmcSchemeSort)}>
                                              <div className="flex items-center justify-end">XIRR %<SortIcon column="xirr_pct" sortConfig={amcSchemeSort} /></div>
                                            </TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {sortData(schemeState.data || [], amcSchemeSort).map((s, si) => {
                                            const sKey = `${amc.amc_name}|${s.scheme_name}`;
                                            const sExpanded = expandedScheme === sKey;
                                            const cState = schemeClients[sKey] || {};
                                            const sxirr = Number(s.xirr_pct || 0);
                                            return (
                                              <React.Fragment key={`scheme-${si}`}>
                                                <TableRow
                                                  className="hover:bg-gray-50 cursor-pointer"
                                                  onClick={() => toggleSchemeRow(amc.amc_name, s.scheme_name)}
                                                  data-testid={`amc-scheme-row-${idx}-${si}`}
                                                >
                                                  <TableCell className="py-1.5 text-xs max-w-[420px]">
                                                    <div className="flex items-center gap-1.5">
                                                      {sExpanded
                                                        ? <ChevronDown className="h-3.5 w-3.5 text-teal-600 shrink-0" />
                                                        : <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
                                                      <span className="truncate" title={s.scheme_name}>{s.scheme_name}</span>
                                                    </div>
                                                  </TableCell>
                                                  <TableCell className="py-1.5 text-xs text-right font-mono">{s.holdings_count}</TableCell>
                                                  <TableCell className="py-1.5 text-xs text-right font-mono">{(s.units || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</TableCell>
                                                  <TableCell className="py-1.5 text-xs text-right font-mono">₹{(s.cost_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                                  <TableCell className="py-1.5 text-xs text-right font-mono">₹{(s.market_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                                  <TableCell className={`py-1.5 text-xs text-right font-mono ${(s.profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {(s.profit_loss || 0) >= 0 ? '+' : ''}₹{Math.abs(s.profit_loss || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                  </TableCell>
                                                  <TableCell className={`py-1.5 text-xs text-right font-mono ${sxirr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {sxirr >= 0 ? '+' : ''}{sxirr.toFixed(2)}%
                                                  </TableCell>
                                                </TableRow>
                                                {sExpanded && (
                                                  <TableRow className="bg-white" data-testid={`amc-scheme-clients-${idx}-${si}`}>
                                                    <TableCell colSpan={7} className="p-0">
                                                      <div className="px-5 py-3 border-l-2 border-blue-400 bg-blue-50/30">
                                                        <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                                          Clients holding {s.scheme_name}
                                                        </div>
                                                        {cState.loading && (
                                                          <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
                                                            <Loader2 className="h-4 w-4 animate-spin" /> Loading clients…
                                                          </div>
                                                        )}
                                                        {!cState.loading && cState.error && (
                                                          <div className="text-sm text-red-500 py-2">Failed to load: {cState.error}</div>
                                                        )}
                                                        {!cState.loading && !cState.error && (cState.data || []).length === 0 && (
                                                          <div className="text-sm text-gray-400 italic py-2">No clients found</div>
                                                        )}
                                                        {!cState.loading && (cState.data || []).length > 0 && (
                                                          <div className="rounded border overflow-x-auto bg-white">
                                                            <Table>
                                                              <TableHeader>
                                                                <TableRow className="bg-gray-100">
                                                                  <TableHead className="h-7 text-[11px] font-semibold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('client_name', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center">Client Name<SortIcon column="client_name" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('pan', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center">PAN<SortIcon column="pan" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('folio_no', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center">Folio<SortIcon column="folio_no" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('sub_broker_name', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center">Sub Broker<SortIcon column="sub_broker_name" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('tenant_name', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center">Tenant<SortIcon column="tenant_name" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('units', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center justify-end">Active Units<SortIcon column="units" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('cost_value', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center justify-end">Cost (₹)<SortIcon column="cost_value" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('market_value', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center justify-end">Market (₹)<SortIcon column="market_value" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('profit_loss', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center justify-end">P/L (₹)<SortIcon column="profit_loss" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                  <TableHead className="h-7 text-[11px] font-semibold text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('xirr_pct', schemeClientSort, setSchemeClientSort)}>
                                                                    <div className="flex items-center justify-end">XIRR %<SortIcon column="xirr_pct" sortConfig={schemeClientSort} /></div>
                                                                  </TableHead>
                                                                </TableRow>
                                                              </TableHeader>
                                                              <TableBody>
                                                                {sortData(cState.data || [], schemeClientSort).map((c, ci) => {
                                                                  const cxirr = Number(c.xirr_pct || 0);
                                                                  return (
                                                                    <TableRow key={`client-${ci}`} className="hover:bg-gray-50">
                                                                      <TableCell className="py-1 text-[11px]">{c.client_name || '—'}</TableCell>
                                                                      <TableCell className="py-1 text-[11px] font-mono">{c.pan || '—'}</TableCell>
                                                                      <TableCell className="py-1 text-[11px] font-mono">{c.folio_no || '—'}</TableCell>
                                                                      <TableCell className="py-1 text-[11px]">{c.sub_broker_name || '—'}</TableCell>
                                                                      <TableCell className="py-1 text-[11px]">{c.tenant_name || '—'}</TableCell>
                                                                      <TableCell className="py-1 text-[11px] text-right font-mono">{(c.units || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</TableCell>
                                                                      <TableCell className="py-1 text-[11px] text-right font-mono">₹{(c.cost_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                                                      <TableCell className="py-1 text-[11px] text-right font-mono">₹{(c.market_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                                                      <TableCell className={`py-1 text-[11px] text-right font-mono ${(c.profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                        {(c.profit_loss || 0) >= 0 ? '+' : ''}₹{Math.abs(c.profit_loss || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                                      </TableCell>
                                                                      <TableCell className={`py-1 text-[11px] text-right font-mono ${cxirr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                        {cxirr >= 0 ? '+' : ''}{cxirr.toFixed(2)}%
                                                                      </TableCell>
                                                                    </TableRow>
                                                                  );
                                                                })}
                                                              </TableBody>
                                                            </Table>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </TableCell>
                                                  </TableRow>
                                                )}
                                              </React.Fragment>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Grand Total Row */}
                    <TableRow className="bg-gray-100 font-semibold border-t-2">
                      <TableCell className="font-bold">GRAND TOTAL</TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        ₹{amcBreakdown.reduce((sum, amc) => sum + (amc.cost_value || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        ₹{amcBreakdown.reduce((sum, amc) => sum + (amc.market_value || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-bold ${
                        amcBreakdown.reduce((sum, amc) => sum + (amc.profit_loss || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {amcBreakdown.reduce((sum, amc) => sum + (amc.profit_loss || 0), 0) >= 0 ? '+' : ''}
                        ₹{Math.abs(amcBreakdown.reduce((sum, amc) => sum + (amc.profit_loss || 0), 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {(() => {
                          const totalCost = amcBreakdown.reduce((sum, amc) => sum + (amc.cost_value || 0), 0);
                          const totalMarket = amcBreakdown.reduce((sum, amc) => sum + (amc.market_value || 0), 0);
                          const pct = totalCost > 0 ? ((Math.pow(totalMarket / totalCost, 1/3) - 1) * 100) : 0;
                          return <span className={pct >= 0 ? 'text-green-600' : 'text-red-600'}>{pct >= 0 ? '+' : ''}{pct.toFixed(2)}%</span>;
                        })()}
                      </TableCell>
                      <TableCell className="text-center">
                        {portfolioMix && (
                          <div className="inline-flex flex-col items-center gap-1 min-w-[110px]">
                            <div className="flex w-24 h-2 rounded-full overflow-hidden bg-slate-100"
                                 title={`Equity ${(portfolioMix.overall?.Equity?.share_pct ?? 0).toFixed(0)}% · Debt ${(portfolioMix.overall?.Debt?.share_pct ?? 0).toFixed(0)}% · Hybrid ${(portfolioMix.overall?.Hybrid?.share_pct ?? 0).toFixed(0)}%`}>
                              <div className="bg-emerald-500 h-full" style={{ width: `${portfolioMix.overall?.Equity?.share_pct ?? 0}%` }} />
                              <div className="bg-amber-500 h-full" style={{ width: `${portfolioMix.overall?.Debt?.share_pct ?? 0}%` }} />
                              <div className="bg-indigo-500 h-full" style={{ width: `${portfolioMix.overall?.Hybrid?.share_pct ?? 0}%` }} />
                            </div>
                            <div className="text-[10px] font-mono text-slate-500">
                              <span className="text-emerald-700">{(portfolioMix.overall?.Equity?.share_pct ?? 0).toFixed(0)}</span>
                              /
                              <span className="text-amber-700">{(portfolioMix.overall?.Debt?.share_pct ?? 0).toFixed(0)}</span>
                              /
                              <span className="text-indigo-700">{(portfolioMix.overall?.Hybrid?.share_pct ?? 0).toFixed(0)}</span>
                            </div>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* AUM Distribution & Holding Pattern - Side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* AUM Distribution Pie Chart */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <IndianRupee className="h-5 w-5 text-purple-600" />
                      AUM Distribution
                    </CardTitle>
                    <CardDescription>Client count by portfolio size (highest to lowest)</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${API_URL}/api/dashboard/aum-distribution-excel`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!response.ok) {
                          const error = await response.json();
                          throw new Error(error.detail || 'Download failed');
                        }
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `AUM_Distribution_${new Date().toISOString().slice(0,10)}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                        toast.success('Excel file downloaded successfully');
                      } catch (error) {
                        toast.error(error.message || 'Failed to download Excel');
                      }
                    }}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {/* Pie Chart */}
                  <div className="h-64 w-48 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={aumBrackets.filter(b => b.count > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="label"
                        >
                          {aumBrackets.filter(b => b.count > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [`${value} clients`, name]}
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Custom Legend - maintains order from backend (highest to lowest) */}
                  <div className="space-y-1.5 flex-1">
                    {aumBrackets.filter(b => b.count > 0).map((bracket, index) => (
                      <div key={bracket.label} className="flex items-center gap-2 text-sm">
                        <div 
                          className="w-3 h-3 rounded-sm flex-shrink-0" 
                          style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                        />
                        <span className="text-gray-700 flex-1">{bracket.label}</span>
                        <span className="text-gray-600 font-medium">{bracket.count}</span>
                        <span className="text-gray-400 w-12 text-right">{bracket.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Exited Tab - Clients who sold units completely */}
        <TabsContent value="exited" className="space-y-6">
          <Card className="border-orange-200 bg-orange-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-orange-800">
                <TrendingUp className="h-5 w-5 text-orange-600" />
                Exited Clients
              </CardTitle>
              <CardDescription>
                Clients who have fully redeemed/sold their investments
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Summary Cards for Exited */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-white rounded-lg border border-orange-200">
                  <div className="text-sm text-gray-500">Exited Clients</div>
                  <div className="text-2xl font-bold text-orange-600">{exitedClients.exited?.count || 0}</div>
                  <div className="text-xs text-gray-400">Sold units completely</div>
                </div>
                <div className="p-4 bg-white rounded-lg border border-orange-200">
                  <div className="text-sm text-gray-500">Total Invested</div>
                  <div className="text-xl font-bold text-gray-800">
                    {formatCurrency(exitedClients.exited?.total_invested || 0)}
                  </div>
                </div>
                <div className="p-4 bg-white rounded-lg border border-orange-200">
                  <div className="text-sm text-gray-500">Total Redeemed</div>
                  <div className="text-xl font-bold text-blue-600">
                    {formatCurrency(exitedClients.exited?.total_redeemed || 0)}
                  </div>
                </div>
                <div className="p-4 bg-white rounded-lg border border-orange-200">
                  <div className="text-sm text-gray-500">Total Profit Made</div>
                  <div className={`text-xl font-bold ${(exitedClients.exited?.total_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(exitedClients.exited?.total_profit || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(exitedClients.exited?.total_profit || 0))}
                  </div>
                </div>
              </div>

              {/* Search Bar and Download Button */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by client name or PAN..."
                    value={exitedSearch}
                    onChange={(e) => setExitedSearch(e.target.value)}
                    className="pl-10 border-orange-200 focus:border-orange-400"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/dashboard/exited-clients-excel`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.detail || 'Download failed');
                      }
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Exited_Clients_${new Date().toISOString().slice(0,10)}.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      a.remove();
                      toast.success('Excel file downloaded successfully');
                    } catch (error) {
                      toast.error(error.message || 'Failed to download Excel');
                    }
                  }}
                  className="gap-2 border-orange-300 hover:bg-orange-50"
                >
                  <Download className="h-4 w-4" />
                  Download Excel
                </Button>
              </div>

              {/* Exited Clients — Mobile Cards */}
              {isMobile && (
                <MobileDashboardCards
                  rows={filteredExitedClients}
                  emptyMsg="No exited clients found"
                  config={{
                    testIdPrefix: "exited",
                    title: (c) => toTitleCase(c.name || ""),
                    subtitle: (c) => `PAN: ${c.pan || "—"}`,
                    chips: (c) => {
                      const out = [];
                      if (c.associate_code) {
                        out.push({ label: c.associate_code, color: "bg-teal-50 text-teal-700 border border-teal-200" });
                      }
                      return out;
                    },
                    stats: [
                      { label: "Invested", value: (c) => mfmtCompact(c.invested_amount) },
                      { label: "Redeemed", value: (c) => mfmtCompact(c.redeemed_amount) },
                      {
                        label: "P/L",
                        value: (c) => `${(c.profit_loss || 0) >= 0 ? "+" : "-"}${mfmtCompact(Math.abs(c.profit_loss || 0))}`,
                        tone: (c) => ((c.profit_loss || 0) >= 0 ? "positive" : "negative"),
                      },
                      {
                        label: "Return %",
                        value: (c) => `${(c.profit_pct || 0) >= 0 ? "+" : ""}${(c.profit_pct || 0).toFixed(2)}%`,
                        tone: (c) => ((c.profit_pct || 0) >= 0 ? "positive" : "negative"),
                      },
                    ],
                    onClick: (c) => toggleDrillDown("exited", c),
                  }}
                />
              )}

              {/* Exited Clients Table — Desktop */}
              <div className={isMobile ? "hidden" : "overflow-x-auto"}>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-orange-100">
                      <TableHead className="font-semibold cursor-pointer hover:bg-orange-200" onClick={() => handleSort('name', exitedSort, setExitedSort)}>
                        <div className="flex items-center">
                          Client Name
                          <SortIcon column="name" sortConfig={exitedSort} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold cursor-pointer hover:bg-orange-200" onClick={() => handleSort('associate_code', exitedSort, setExitedSort)}>
                        <div className="flex items-center">
                          Associate
                          <SortIcon column="associate_code" sortConfig={exitedSort} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-right cursor-pointer hover:bg-orange-200" onClick={() => handleSort('invested_amount', exitedSort, setExitedSort)}>
                        <div className="flex items-center justify-end">
                          Invested Amount
                          <SortIcon column="invested_amount" sortConfig={exitedSort} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-right cursor-pointer hover:bg-orange-200" onClick={() => handleSort('redeemed_amount', exitedSort, setExitedSort)}>
                        <div className="flex items-center justify-end">
                          Redeemed Amount
                          <SortIcon column="redeemed_amount" sortConfig={exitedSort} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-right cursor-pointer hover:bg-orange-200" onClick={() => handleSort('profit_loss', exitedSort, setExitedSort)}>
                        <div className="flex items-center justify-end">
                          Profit/Loss
                          <SortIcon column="profit_loss" sortConfig={exitedSort} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-right cursor-pointer hover:bg-orange-200" onClick={() => handleSort('profit_pct', exitedSort, setExitedSort)}>
                        <div className="flex items-center justify-end">
                          Return %
                          <SortIcon column="profit_pct" sortConfig={exitedSort} />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredExitedClients.slice((pgExited - 1) * pgSize, pgExited * pgSize).map((client, idx) => (
                      <React.Fragment key={`exited-${client.pan || idx}-${idx}`}>
                      <TableRow className="hover:bg-orange-50 cursor-pointer" onClick={() => toggleDrillDown('exited', client)} data-testid={`exited-row-${idx}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <DrillCaret tab="exited" client={client} />
                            <TooltipProvider>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <div className="font-medium cursor-pointer">{toTitleCase(client.name)}</div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="font-mono">{client.pan}</p>
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                        <TableCell>
                          {client.associate_code ? (
                            <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-mono text-teal-700" title={client.associate_name || ""}>
                              {client.associate_code}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ₹{(client.invested_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right font-mono text-blue-600">
                          ₹{(client.redeemed_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${(client.profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(client.profit_loss || 0) >= 0 ? '+' : ''}₹{Math.abs(client.profit_loss || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${(client.profit_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(client.profit_pct || 0) >= 0 ? '+' : ''}{(client.profit_pct || 0).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                      {openDrillKey === rowKeyForClient('exited', client) && (
                        <DrillDownSubRow tab="exited" client={client} colSpan={6} columns={[
                          { key: 'invested_amount', label: 'Invested', align: 'right', render: v => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}` },
                          { key: 'redeemed_amount', label: 'Redeemed', align: 'right', render: v => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}` },
                          { key: 'profit_loss', label: 'P/L', align: 'right', render: v => `${(v||0) >= 0 ? '+' : ''}₹${Math.abs(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`, colorFn: v => (v||0) >= 0 ? 'text-green-600' : 'text-red-600' },
                          { key: 'txn_count', label: '# Txns', align: 'right' },
                        ]} />
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  total={filteredExitedClients.length}
                  page={pgExited}
                  pageSize={pgSize}
                  onPageChange={setPgExited}
                  onPageSizeChange={setPgSize}
                  label="exited clients"
                  testId="exited-pagination"
                />
              </div>
              
              {filteredExitedClients.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>{exitedSearch ? 'No clients found matching your search' : 'No exited clients found'}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Multi Broker Tab */}
        {/* Multi Broker Tab */}
        <TabsContent value="multi_broker" className="space-y-6" data-testid="multi-broker-content">
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-violet-600" />
                  Multi Broker Clients
                  <Badge variant="secondary" className="bg-violet-100 text-violet-700">
                    {dashboardData?.multi_broker?.count || 0} clients
                  </Badge>
                </h3>
              </div>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input type="text" placeholder="Search by name, PAN, folio, or scheme..." value={multiBrokerSearch} onChange={(e) => setMultiBrokerSearch(e.target.value)} className="pl-10 border-violet-200 focus:border-violet-400" />
                </div>
              </div>

              {filteredMultiBroker.length > 0 ? (
                <>
                {isMobile && (
                  <MobileDashboardCards
                    rows={filteredMultiBroker}
                    emptyMsg="No multi-broker clients found"
                    config={{
                      testIdPrefix: "multi-broker",
                      title: (c) => toTitleCase(c.name || ""),
                      subtitle: (c) => `PAN: ${c.pan || "—"}`,
                      chips: (c) => (c.associate_code ? [{ label: c.associate_code, color: "bg-teal-50 text-teal-700 border border-teal-200" }] : []),
                      stats: [
                        { label: "Folios", value: (c) => (c.folios_count || (c.folios?.length || 0)).toLocaleString('en-IN') },
                        { label: "Holdings", value: (c) => (c.holdings_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }) },
                        { label: "Our Units", value: (c) => (c.our_active_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }) },
                        { label: "Other Broker", value: (c) => (c.other_broker_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }), tone: () => "negative" },
                      ],
                      onClick: (c) => setExpandedMultiBroker(expandedMultiBroker === (c.pan || `name`) ? null : (c.pan || `name`)),
                    }}
                  />
                )}
                <div className={isMobile ? "hidden" : "rounded-lg border overflow-hidden"}>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">#</TableHead>
                        <TableHead className="font-semibold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name', multiBrokerSort, setMultiBrokerSort)}><div className="flex items-center">Investor Name<SortIcon column="name" sortConfig={multiBrokerSort} /></div></TableHead>
                        <TableHead className="font-semibold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('associate_code', multiBrokerSort, setMultiBrokerSort)}><div className="flex items-center">Associate<SortIcon column="associate_code" sortConfig={multiBrokerSort} /></div></TableHead>
                        <TableHead className="font-semibold">PAN</TableHead>
                        <TableHead className="font-semibold text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('folios_count', multiBrokerSort, setMultiBrokerSort)}><div className="flex items-center justify-end">Folios<SortIcon column="folios_count" sortConfig={multiBrokerSort} /></div></TableHead>
                        <TableHead className="font-semibold text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('holdings_units', multiBrokerSort, setMultiBrokerSort)}><div className="flex items-center justify-end">Holdings (AUM)<SortIcon column="holdings_units" sortConfig={multiBrokerSort} /></div></TableHead>
                        <TableHead className="font-semibold text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('our_active_units', multiBrokerSort, setMultiBrokerSort)}><div className="flex items-center justify-end">Our Active Units<SortIcon column="our_active_units" sortConfig={multiBrokerSort} /></div></TableHead>
                        <TableHead className="font-semibold text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('other_broker_units', multiBrokerSort, setMultiBrokerSort)}><div className="flex items-center justify-end">Other Broker Units<SortIcon column="other_broker_units" sortConfig={multiBrokerSort} /></div></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMultiBroker.slice((pgMultiBroker - 1) * pgSize, pgMultiBroker * pgSize).map((client, idx) => {
                        const rowKey = client.pan || `name-${idx}`;
                        const isExpanded = expandedMultiBroker === rowKey;
                        return (
                          <React.Fragment key={`mb-${rowKey}-${idx}`}>
                            <TableRow
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => setExpandedMultiBroker(isExpanded ? null : rowKey)}
                              data-testid={`multi-broker-row-${client.pan || idx}`}
                            >
                              <TableCell className="font-mono text-sm text-gray-500">
                                <span className="inline-flex items-center gap-1">
                                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  {idx + 1}
                                </span>
                              </TableCell>
                              <TableCell className="font-medium">{toTitleCase(client.name)}</TableCell>
                              <TableCell>{client.associate_code ? <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-mono text-teal-700" title={client.associate_name || ""}>{client.associate_code}</span> : <span className="text-gray-300 text-xs">—</span>}</TableCell>
                              <TableCell className="font-mono text-sm">{client.pan}</TableCell>
                              <TableCell className="text-right font-mono">{client.folios_count || (client.folios?.length || 0)}</TableCell>
                              <TableCell className="text-right font-mono">{(client.holdings_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}</TableCell>
                              <TableCell className="text-right font-mono">{(client.our_active_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}</TableCell>
                              <TableCell className="text-right font-mono text-red-600 font-medium">{(client.other_broker_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}</TableCell>
                            </TableRow>
                            {isExpanded && (client.folios || []).length > 0 && (
                              <TableRow className="bg-gray-50/50">
                                <TableCell colSpan={8} className="p-0">
                                  <div className="px-6 py-3">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Folio + Scheme breakdown</div>
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-gray-600">
                                          <th className="text-left py-1 font-medium">Folio</th>
                                          <th className="text-left py-1 font-medium">Scheme</th>
                                          <th className="text-left py-1 font-medium">Source</th>
                                          <th className="text-right py-1 font-medium">Holdings (AUM)</th>
                                          <th className="text-right py-1 font-medium">Our Active</th>
                                          <th className="text-right py-1 font-medium">Other Broker</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(client.folios || []).map((f, fi) => (
                                          <tr key={fi} className="border-t border-gray-200">
                                            <td className="py-1 font-mono">{f.folio_no}</td>
                                            <td className="py-1">{f.scheme_name}</td>
                                            <td className="py-1 font-mono">{f.source}</td>
                                            <td className="py-1 text-right font-mono">{(f.holdings_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}</td>
                                            <td className="py-1 text-right font-mono">{(f.our_active_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}</td>
                                            <td className="py-1 text-right font-mono text-red-600">{(f.other_broker_units || 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    total={filteredMultiBroker.length}
                    page={pgMultiBroker}
                    pageSize={pgSize}
                    onPageChange={setPgMultiBroker}
                    onPageSizeChange={setPgSize}
                    label="multi-broker clients"
                    testId="multi-broker-pagination"
                  />
                </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <GitBranch className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No multi-broker clients found</p>
                  <p className="text-sm mt-1">Investors holding funds through multiple distributors will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reduced Portfolio Tab — active clients whose current AUM is < 50%
            of historical gross invested AND who have actually redeemed. */}
        <TabsContent value="reduced_portfolio" className="space-y-6" data-testid="reduced-portfolio-content">
          <Card className="border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                  Reduced Portfolio Clients
                </h3>
                <Badge variant="outline" className="bg-amber-50">
                  Active · current AUM &lt; 50% of historical invested
                </Badge>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Clients</div>
                  <div className="text-2xl font-bold text-amber-700">{dashboardData?.reduced_portfolio?.count || 0}</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Total Invested</div>
                  <div className="text-xl font-bold text-blue-700">{formatCurrency(dashboardData?.reduced_portfolio?.total_invested || 0)}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Current AUM</div>
                  <div className="text-xl font-bold text-purple-700">{formatCurrency(dashboardData?.reduced_portfolio?.total_current_aum || 0)}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Total Drawdown</div>
                  <div className="text-xl font-bold text-red-700">{formatCurrency(dashboardData?.reduced_portfolio?.total_drawdown || 0)}</div>
                </div>
              </div>

              <div className="mb-3 flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="Search by PAN or name…"
                  value={reducedSearch}
                  onChange={(e) => setReducedSearch(e.target.value)}
                  className="max-w-sm"
                  data-testid="reduced-portfolio-search"
                />
                <span className="text-xs text-gray-500">
                  Showing {(() => {
                    const list = (dashboardData?.reduced_portfolio?.clients || []).filter(c => {
                      if (!reducedSearch.trim()) return true;
                      const q = reducedSearch.toLowerCase();
                      return (c.pan || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
                    });
                    return list.length;
                  })()} of {dashboardData?.reduced_portfolio?.count || 0}
                </span>
                <div className="ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 border-amber-300 hover:bg-amber-50"
                    data-testid="reduced-portfolio-csv"
                    onClick={() => {
                      const rows = sortData(
                        (dashboardData?.reduced_portfolio?.clients || []).filter(c => {
                          if (!reducedSearch.trim()) return true;
                          const q = reducedSearch.toLowerCase();
                          return (c.pan || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
                        }),
                        reducedSort,
                      );
                      downloadCsv(rows, [
                        { key: 'pan', label: 'PAN' },
                        { key: 'name', label: 'Name' },
                        { key: 'associate_code', label: 'Associate Code' },
                        { key: 'associate_name', label: 'Associate Name' },
                        { key: 'invested_amount', label: 'Invested', fmt: v => Number(v || 0).toFixed(2) },
                        { key: 'redeemed_amount', label: 'Redeemed', fmt: v => Number(v || 0).toFixed(2) },
                        { key: 'current_aum', label: 'Current AUM', fmt: v => Number(v || 0).toFixed(2) },
                        { key: 'drawdown_amount', label: 'Drawdown', fmt: v => Number(v || 0).toFixed(2) },
                        { key: 'drawdown_pct', label: 'Drawdown %' },
                        { key: 'last_txn_date', label: 'Last Txn' },
                      ], `Reduced_Portfolio_${new Date().toISOString().slice(0,10)}.csv`);
                    }}
                  >
                    <Download className="h-4 w-4" />
                    Download CSV
                  </Button>
                </div>
              </div>

              {isMobile && (
                <MobileDashboardCards
                  rows={sortData(
                    (dashboardData?.reduced_portfolio?.clients || []).filter(c => {
                      if (!reducedSearch.trim()) return true;
                      const q = reducedSearch.toLowerCase();
                      return (c.pan || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
                    }),
                    reducedSort
                  )}
                  emptyMsg="No reduced-portfolio clients found"
                  config={{
                    testIdPrefix: "reduced",
                    title: (c) => toTitleCase(c.name || ""),
                    subtitle: (c) => `PAN: ${c.pan || "—"} · Last txn: ${c.last_txn_date || '—'}`,
                    chips: (c) => {
                      const out = [];
                      if (c.associate_code) out.push({ label: c.associate_code, color: "bg-teal-50 text-teal-700 border border-teal-200" });
                      out.push({ label: `${c.drawdown_pct}%↓`, color: "bg-red-50 text-red-700 border border-red-200" });
                      return out;
                    },
                    stats: [
                      { label: "Invested", value: (c) => mfmtCompact(c.invested_amount) },
                      { label: "Redeemed", value: (c) => mfmtCompact(c.redeemed_amount) },
                      { label: "Current AUM", value: (c) => mfmtCompact(c.current_aum) },
                      { label: "Drawdown", value: (c) => mfmtCompact(c.drawdown_amount), tone: () => "negative" },
                    ],
                    onClick: (c) => toggleDrillDown('reduced_portfolio', c),
                  }}
                />
              )}
              <div className={isMobile ? "hidden" : "border rounded-lg overflow-x-auto max-h-[600px]"} data-testid="reduced-portfolio-table">
                <Table>
                  <TableHeader className="bg-amber-50 sticky top-0 z-10">
                    <TableRow>
                      <TableHead className="cursor-pointer hover:bg-amber-100" onClick={() => handleSort('pan', reducedSort, setReducedSort)} data-testid="reduced-sort-pan"><div className="flex items-center">PAN<SortIcon column="pan" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="cursor-pointer hover:bg-amber-100" onClick={() => handleSort('name', reducedSort, setReducedSort)} data-testid="reduced-sort-name"><div className="flex items-center">Name<SortIcon column="name" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="cursor-pointer hover:bg-amber-100" onClick={() => handleSort('associate_code', reducedSort, setReducedSort)} data-testid="reduced-sort-associate"><div className="flex items-center">Associate<SortIcon column="associate_code" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="text-right cursor-pointer hover:bg-amber-100" onClick={() => handleSort('invested_amount', reducedSort, setReducedSort)} data-testid="reduced-sort-invested"><div className="flex items-center justify-end">Invested<SortIcon column="invested_amount" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="text-right cursor-pointer hover:bg-amber-100" onClick={() => handleSort('redeemed_amount', reducedSort, setReducedSort)} data-testid="reduced-sort-redeemed"><div className="flex items-center justify-end">Redeemed<SortIcon column="redeemed_amount" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="text-right cursor-pointer hover:bg-amber-100" onClick={() => handleSort('current_aum', reducedSort, setReducedSort)} data-testid="reduced-sort-aum"><div className="flex items-center justify-end">Current AUM<SortIcon column="current_aum" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="text-right cursor-pointer hover:bg-amber-100" onClick={() => handleSort('drawdown_amount', reducedSort, setReducedSort)} data-testid="reduced-sort-drawdown"><div className="flex items-center justify-end">Drawdown<SortIcon column="drawdown_amount" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="text-right cursor-pointer hover:bg-amber-100" onClick={() => handleSort('drawdown_pct', reducedSort, setReducedSort)} data-testid="reduced-sort-drawdown-pct"><div className="flex items-center justify-end">Drawdown %<SortIcon column="drawdown_pct" sortConfig={reducedSort} /></div></TableHead>
                      <TableHead className="text-right cursor-pointer hover:bg-amber-100" onClick={() => handleSort('last_txn_date', reducedSort, setReducedSort)} data-testid="reduced-sort-last"><div className="flex items-center justify-end">Last Txn<SortIcon column="last_txn_date" sortConfig={reducedSort} /></div></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const _reducedAll = sortData(
                        (dashboardData?.reduced_portfolio?.clients || []).filter(c => {
                          if (!reducedSearch.trim()) return true;
                          const q = reducedSearch.toLowerCase();
                          return (c.pan || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
                        }),
                        reducedSort
                      );
                      return _reducedAll.slice((pgReducedPortfolio - 1) * pgSize, pgReducedPortfolio * pgSize).map((c, i) => (
                      <React.Fragment key={`${c.pan}-${i}`}>
                      <TableRow className="hover:bg-amber-50/40 cursor-pointer" onClick={() => toggleDrillDown('reduced_portfolio', c)} data-testid={`reduced-row-${i}`}>
                        <TableCell className="font-mono text-xs"><DrillCaret tab="reduced_portfolio" client={c} /> {c.pan}</TableCell>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell>{c.associate_code ? <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-mono text-teal-700" title={c.associate_name || ""}>{c.associate_code}</span> : <span className="text-gray-300 text-xs">—</span>}</TableCell>
                        <TableCell className="text-right">{formatCurrency(c.invested_amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(c.redeemed_amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(c.current_aum)}</TableCell>
                        <TableCell className="text-right text-red-600 font-semibold">{formatCurrency(c.drawdown_amount)}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{c.drawdown_pct}%</Badge>
                        </TableCell>
                        <TableCell className="text-right text-xs text-gray-500">{c.last_txn_date || '—'}</TableCell>
                      </TableRow>
                      {openDrillKey === rowKeyForClient('reduced_portfolio', c) && (
                        <DrillDownSubRow tab="reduced_portfolio" client={c} colSpan={9} columns={[
                          { key: 'balance_units', label: 'Units', align: 'right', render: v => (v||0).toLocaleString('en-IN', { minimumFractionDigits: 3 }) },
                          { key: 'cost_value', label: 'Cost', align: 'right', render: v => formatCurrency(v) },
                          { key: 'market_value', label: 'Market', align: 'right', render: v => formatCurrency(v) },
                          { key: 'profit_loss', label: 'P/L', align: 'right', render: v => formatCurrency(v), colorFn: v => (v||0) >= 0 ? 'text-green-600' : 'text-red-600' },
                          { key: 'xirr_pct', label: 'XIRR %', align: 'right', render: v => `${(v||0).toFixed(2)}%` },
                        ]} />
                      )}
                      </React.Fragment>
                    ));
                    })()}
                    {(dashboardData?.reduced_portfolio?.clients || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-gray-400">
                          No reduced-portfolio clients found. Click <strong>Refresh Dashboard Cache</strong> on the Reset DB page if this is unexpected.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  total={(() => {
                    const q = reducedSearch.toLowerCase();
                    return (dashboardData?.reduced_portfolio?.clients || []).filter(c => !q ||
                      (c.pan || "").toLowerCase().includes(q) ||
                      (c.name || "").toLowerCase().includes(q)
                    ).length;
                  })()}
                  page={pgReducedPortfolio}
                  pageSize={pgSize}
                  onPageChange={setPgReducedPortfolio}
                  onPageSizeChange={setPgSize}
                  label="reduced-portfolio clients"
                  testId="reduced-portfolio-pagination"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Underperformers Tab — folio+ISIN level: 5y+ holding & XIRR < 14% */}
        <TabsContent value="underperformers" className="space-y-6" data-testid="underperformers-content">
          <Card className="border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-red-600 rotate-180" />
                  Underperforming Holdings
                </h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-xs">
                    <label className="text-gray-600">Min holding (yrs)</label>
                    <select
                      value={underMinYears}
                      onChange={(e) => setUnderMinYears(Number(e.target.value))}
                      className="border rounded px-2 py-1 text-xs bg-white"
                      data-testid="under-min-years"
                    >
                      <option value={1}>1+</option>
                      <option value={2}>2+</option>
                      <option value={3}>3+</option>
                      <option value={5}>5+</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <label className="text-gray-600">Max XIRR (%)</label>
                    <Input
                      type="number"
                      min={0}
                      max={14}
                      step={0.5}
                      value={underMaxXirr}
                      onChange={(e) => {
                        // Cap at 14% — anything ≥ 14 is not an under-performer
                        // by definition, so the input only allows tightening the
                        // threshold (e.g. focus on <12% or <11% rows).
                        const raw = Number(e.target.value) || 0;
                        setUnderMaxXirr(Math.min(14, Math.max(0, raw)));
                      }}
                      className="w-20 h-7 text-xs"
                      data-testid="under-max-xirr"
                    />
                  </div>
                  <div className="flex items-center gap-1 text-[10px]">
                    <span className="inline-block w-3 h-3 bg-red-200 rounded border border-red-400" />
                    <span className="text-gray-600">&lt;11%</span>
                    <span className="inline-block w-3 h-3 bg-amber-200 rounded border border-amber-400 ml-2" />
                    <span className="text-gray-600">11–14%</span>
                  </div>
                </div>
              </div>

              {underperformersLoading && (
                <div className="text-center py-12 text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
                  Loading underperformers from active_units…
                </div>
              )}

              {!underperformersLoading && underperformers && (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Clients</div>
                      <div className="text-2xl font-bold text-red-700">{underperformers.count || 0}</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Total Cost</div>
                      <div className="text-xl font-bold text-blue-700">{formatCurrency(underperformers.total_cost || 0)}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Withdrawals + Dividend + Market</div>
                      <div className="text-xl font-bold text-purple-700">{formatCurrency((underperformers.total_market_div || underperformers.total_market || 0) + (underperformers.total_redemptions || 0))}</div>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Lifetime Dividends</div>
                      <div className="text-xl font-bold text-teal-700">{formatCurrency(underperformers.total_dividends || 0)}</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Total P/L</div>
                      <div className={`text-xl font-bold ${(underperformers.total_profit_loss || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {(underperformers.total_profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(underperformers.total_profit_loss || 0))}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3 flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="Search by PAN or name…"
                      value={underSearch}
                      onChange={(e) => setUnderSearch(e.target.value)}
                      className="max-w-sm"
                      data-testid="underperformers-search"
                    />
                    <span className="text-xs text-gray-500">
                      Showing {(() => {
                        const q = underSearch.toLowerCase();
                        const list = (underperformers.clients || []).filter(c => !q ||
                          (c.client_pan || '').toLowerCase().includes(q) ||
                          (c.client_name || '').toLowerCase().includes(q));
                        return list.length;
                      })()} of {underperformers.count || 0} clients
                    </span>
                    <div className="ml-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 border-red-300 hover:bg-red-50"
                        data-testid="underperformers-csv"
                        onClick={() => {
                          const q = underSearch.toLowerCase();
                          const rows = sortData(
                            (underperformers.clients || []).filter(c => !q ||
                              (c.client_pan || '').toLowerCase().includes(q) ||
                              (c.client_name || '').toLowerCase().includes(q)),
                            underSort,
                          );
                          downloadCsv(rows, [
                            { key: 'client_pan', label: 'PAN' },
                            { key: 'client_name', label: 'Client Name' },
                            { key: 'associate_code', label: 'Associate Code' },
                            { key: 'associate_name', label: 'Associate Name' },
                            { key: 'holdings_count', label: 'Holdings' },
                            { key: 'schemes_count', label: 'Schemes' },
                            { key: 'total_cost', label: 'Cost', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_market', label: 'Market', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_dividends', label: 'Lifetime IDCW', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_redemptions', label: 'Lifetime Redemptions', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_market_div', label: 'Market + Div', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'withdrawals_div_market', label: 'Withdrawals + Dividend + Market', fmt: (v, row) => (Number(row?.total_market_div || row?.total_market || 0) + Number(row?.total_redemptions || 0)).toFixed(2) },
                            { key: 'total_profit_loss', label: 'P/L', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'weighted_xirr_pct', label: 'XIRR %' },
                          ], `Underperformers_${new Date().toISOString().slice(0,10)}.csv`);
                        }}
                      >
                        <Download className="h-4 w-4" />
                        Download CSV
                      </Button>
                    </div>
                  </div>

                  {isMobile && (
                    <MobileDashboardCards
                      rows={sortData(
                        (underperformers.clients || []).filter(c => {
                          if (!underSearch.trim()) return true;
                          const q = underSearch.toLowerCase();
                          return (c.client_pan || '').toLowerCase().includes(q) ||
                                 (c.client_name || '').toLowerCase().includes(q);
                        }),
                        underSort
                      )}
                      emptyMsg="No underperformers found"
                      config={{
                        testIdPrefix: "underperformers",
                        title: (c) => toTitleCase(c.client_name || ""),
                        subtitle: (c) => `PAN: ${c.client_pan || "—"}`,
                        chips: (c) => {
                          const out = [];
                          if (c.associate_code) out.push({ label: c.associate_code, color: "bg-teal-50 text-teal-700 border border-teal-200" });
                          const x = Number(c.weighted_xirr_pct ?? 0);
                          out.push({ label: `${x.toFixed(2)}% XIRR`, color: x < 11 ? "bg-red-100 text-red-700 border border-red-200" : "bg-amber-100 text-amber-700 border border-amber-200" });
                          return out;
                        },
                        stats: [
                          { label: "Cost", value: (c) => mfmtCompact(c.total_cost) },
                          { label: "Withdrawals+Dividend+Market", value: (c) => mfmtCompact((c.total_market_div || c.total_market || 0) + (c.total_redemptions || 0)) },
                          { label: "P/L", value: (c) => `${(c.total_profit_loss || 0) >= 0 ? '+' : '-'}${mfmtCompact(Math.abs(c.total_profit_loss || 0))}`, tone: (c) => (c.total_profit_loss || 0) >= 0 ? "positive" : "negative" },
                          { label: "Holdings", value: (c) => `${c.holdings_count || 0} · ${c.schemes_count || 0} schm` },
                        ],
                        onClick: (c) => toggleDrillDown('underperformers', { ...c, pan: c.client_pan, name: c.client_name }),
                      }}
                    />
                  )}
                  <div className={isMobile ? "hidden" : "border rounded-lg overflow-x-auto max-h-[600px]"}>
                    <Table>
                      <TableHeader className="bg-red-50 sticky top-0 z-10">
                        <TableRow>
                          <TableHead className="cursor-pointer hover:bg-red-100" onClick={() => handleSort('client_pan', underSort, setUnderSort)} data-testid="under-sort-pan"><div className="flex items-center">PAN<SortIcon column="client_pan" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="cursor-pointer hover:bg-red-100" onClick={() => handleSort('client_name', underSort, setUnderSort)} data-testid="under-sort-name"><div className="flex items-center">Client Name<SortIcon column="client_name" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="cursor-pointer hover:bg-red-100" onClick={() => handleSort('associate_code', underSort, setUnderSort)} data-testid="under-sort-associate"><div className="flex items-center">Associate<SortIcon column="associate_code" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-center cursor-pointer hover:bg-red-100" onClick={() => handleSort('holdings_count', underSort, setUnderSort)} data-testid="under-sort-holdings"><div className="flex items-center justify-center">Holdings<SortIcon column="holdings_count" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-center cursor-pointer hover:bg-red-100" onClick={() => handleSort('schemes_count', underSort, setUnderSort)} data-testid="under-sort-schemes"><div className="flex items-center justify-center">Schemes<SortIcon column="schemes_count" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-red-100" onClick={() => handleSort('total_cost', underSort, setUnderSort)} data-testid="under-sort-cost"><div className="flex items-center justify-end">Cost<SortIcon column="total_cost" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-red-100" onClick={() => handleSort('total_market_div', underSort, setUnderSort)} data-testid="under-sort-market"><div className="flex items-center justify-end">Withdrawals + Dividend + Market<SortIcon column="total_market_div" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-red-100" onClick={() => handleSort('total_dividends', underSort, setUnderSort)} data-testid="under-sort-div"><div className="flex items-center justify-end">Dividend<SortIcon column="total_dividends" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-red-100" onClick={() => handleSort('total_redemptions', underSort, setUnderSort)} data-testid="under-sort-red"><div className="flex items-center justify-end">Redemption<SortIcon column="total_redemptions" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-red-100" onClick={() => handleSort('total_market', underSort, setUnderSort)} data-testid="under-sort-market-only"><div className="flex items-center justify-end">Market Value<SortIcon column="total_market" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-red-100" onClick={() => handleSort('total_profit_loss', underSort, setUnderSort)} data-testid="under-sort-pl"><div className="flex items-center justify-end">P/L<SortIcon column="total_profit_loss" sortConfig={underSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-red-100" onClick={() => handleSort('weighted_xirr_pct', underSort, setUnderSort)} data-testid="under-sort-xirr"><div className="flex items-center justify-end">Wtd. XIRR<SortIcon column="weighted_xirr_pct" sortConfig={underSort} /></div></TableHead>
                          <TableHead>Sources</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const _underAll = sortData(
                            (underperformers.clients || []).filter(c => {
                              if (!underSearch.trim()) return true;
                              const q = underSearch.toLowerCase();
                              return (c.client_pan || '').toLowerCase().includes(q) ||
                                     (c.client_name || '').toLowerCase().includes(q);
                            }),
                            underSort,
                          );
                          return _underAll.slice((pgUnderperformers - 1) * pgSize, pgUnderperformers * pgSize).map((c, i) => (
                          <React.Fragment key={`${c.client_pan || c.group_key}-${i}`}>
                          {(() => {
                            // Underperformers rows carry `client_pan` / `client_name`;
                            // toggleDrillDown's row key keys on `pan` / `name`, so we
                            // remap once here to keep onClick, caret render and the
                            // open-state comparison perfectly consistent.
                            const cMapped = { ...c, pan: c.client_pan, name: c.client_name };
                            const drillOpen = openDrillKey === rowKeyForClient('underperformers', cMapped);
                            return (
                              <>
                          <TableRow
                            className={(() => {
                              const x = Number(c.weighted_xirr_pct ?? 0);
                              // The API never returns rows ≥ 14% (that's the
                              // definition of an under-performer cap), so we
                              // only need two bands: red <11, yellow 11-14.
                              if (x < 11) return "bg-red-50 hover:bg-red-100 cursor-pointer";
                              return "bg-amber-50 hover:bg-amber-100 cursor-pointer";
                            })()}
                            onClick={() => toggleDrillDown('underperformers', cMapped)}
                            data-testid={`underperformers-row-${i}`}
                          >
                            <TableCell className="font-mono text-xs">
                              <DrillCaret tab="underperformers" client={cMapped} />{' '}
                              {c.client_pan || <span className="text-gray-400 italic">— grouped by name —</span>}
                            </TableCell>
                            <TableCell className="font-medium">{toTitleCase(c.client_name)}</TableCell>
                            <TableCell>{c.associate_code ? <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-mono text-teal-700" title={c.associate_name || ""}>{c.associate_code}</span> : <span className="text-gray-300 text-xs">—</span>}</TableCell>
                            <TableCell className="text-center font-mono">{c.holdings_count}</TableCell>
                            <TableCell className="text-center font-mono text-gray-600">{c.schemes_count}</TableCell>
                            <TableCell className="text-right">{formatCurrency(c.total_cost || 0)}</TableCell>
                            <TableCell className="text-right" title={`Market: ${formatCurrency(c.total_market||0)} + Lifetime IDCW: ${formatCurrency(c.total_dividends||0)} + Lifetime Withdrawals: ${formatCurrency(c.total_redemptions||0)}`}>{formatCurrency((c.total_market_div || c.total_market || 0) + (c.total_redemptions || 0))}</TableCell>
                            <TableCell className="text-right text-emerald-700">{(c.total_dividends || 0) > 0 ? formatCurrency(c.total_dividends) : <span className="text-gray-300">—</span>}</TableCell>
                            <TableCell className="text-right text-indigo-700">{(c.total_redemptions || 0) > 0 ? formatCurrency(c.total_redemptions) : <span className="text-gray-300">—</span>}</TableCell>
                            <TableCell className="text-right text-blue-700">{formatCurrency(c.total_market || 0)}</TableCell>
                            <TableCell className={`text-right ${(c.total_profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(c.total_profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(c.total_profit_loss || 0))}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline" className={(c.weighted_xirr_pct || 0) < 0 ? 'bg-red-100 text-red-800' : 'bg-amber-50 text-amber-800'}>
                                {c.weighted_xirr_pct?.toFixed?.(2) ?? '—'}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-gray-600">{(c.sources || []).join(', ')}</TableCell>
                          </TableRow>
                          {drillOpen && (
                            <DrillDownSubRow tab="underperformers" client={cMapped} colSpan={13} columns={[
                              { key: 'balance_units', label: 'Units', align: 'right', render: v => (v||0).toLocaleString('en-IN', { minimumFractionDigits: 3 }) },
                              { key: 'cost_value', label: 'Cost', align: 'right', render: v => formatCurrency(v) },
                              { key: '_wdm', label: 'Withdrawals + Dividend + Market', align: 'right',
                                render: (_v, row) => formatCurrency((row?.market_value || 0) + (row?.total_dividends || 0) + (row?.total_redemptions || 0)) },
                              { key: 'total_dividends', label: 'Dividend', align: 'right', render: v => (v||0) > 0 ? formatCurrency(v) : '—' },
                              { key: 'total_redemptions', label: 'Redemption', align: 'right', render: v => (v||0) > 0 ? formatCurrency(v) : '—' },
                              { key: 'market_value', label: 'Market Value', align: 'right', render: v => formatCurrency(v) },
                              { key: 'profit_loss', label: 'P/L', align: 'right', render: v => formatCurrency(v), colorFn: v => (v||0) >= 0 ? 'text-green-600' : 'text-red-600' },
                              { key: 'xirr_pct', label: 'Wtd. XIRR', align: 'right', render: v => `${(v||0).toFixed(2)}%` },
                            ]} />
                          )}
                              </>
                            );
                          })()}
                          </React.Fragment>
                        ));
                        })()}
                        {(underperformers.clients || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={13} className="text-center py-12 text-gray-400">
                              No underperformers found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      total={(() => {
                        const q = underSearch.toLowerCase();
                        return (underperformers.clients || []).filter(c => !q ||
                          (c.client_pan || '').toLowerCase().includes(q) ||
                          (c.client_name || '').toLowerCase().includes(q)).length;
                      })()}
                      page={pgUnderperformers}
                      pageSize={pgSize}
                      onPageChange={setPgUnderperformers}
                      onPageSizeChange={setPgSize}
                      label="clients"
                      testId="underperformers-pagination"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outperformers Tab — mirror of Underperformers; XIRR ≥ 14% */}
        <TabsContent value="outperformers" className="space-y-6" data-testid="outperformers-content">
          <Card className="border-emerald-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  Outperforming Holdings
                </h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-xs">
                    <label className="text-gray-600">Min holding (yrs)</label>
                    <select
                      value={outMinYears}
                      onChange={(e) => setOutMinYears(Number(e.target.value))}
                      className="border rounded px-2 py-1 text-xs bg-white"
                      data-testid="out-min-years"
                    >
                      <option value={1}>1+</option>
                      <option value={2}>2+</option>
                      <option value={3}>3+</option>
                      <option value={5}>5+</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <label className="text-gray-600">Min XIRR (%)</label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={outMinXirr}
                      onChange={(e) => {
                        // Floor anything from 0%; user tightens by raising
                        // the bar (e.g. >18% for elite performers only).
                        const raw = Number(e.target.value) || 0;
                        setOutMinXirr(Math.max(0, Math.min(100, raw)));
                      }}
                      className="w-20 h-7 text-xs"
                      data-testid="out-min-xirr"
                    />
                  </div>
                  <div className="flex items-center gap-1 text-[10px]">
                    <span className="inline-block w-3 h-3 bg-emerald-200 rounded border border-emerald-400" />
                    <span className="text-gray-600">14–18%</span>
                    <span className="inline-block w-3 h-3 bg-green-300 rounded border border-green-500 ml-2" />
                    <span className="text-gray-600">&gt;18%</span>
                  </div>
                </div>
              </div>

              {outperformersLoading && (
                <div className="text-center py-12 text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
                  Loading outperformers from active_units…
                </div>
              )}

              {!outperformersLoading && outperformers && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Clients</div>
                      <div className="text-2xl font-bold text-emerald-700">{outperformers.count || 0}</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Total Cost</div>
                      <div className="text-xl font-bold text-blue-700">{formatCurrency(outperformers.total_cost || 0)}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Withdrawals + Dividend + Market</div>
                      <div className="text-xl font-bold text-purple-700">{formatCurrency((outperformers.total_market_div || outperformers.total_market || 0) + (outperformers.total_redemptions || 0))}</div>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Lifetime Dividends</div>
                      <div className="text-xl font-bold text-teal-700">{formatCurrency(outperformers.total_dividends || 0)}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Total P/L</div>
                      <div className={`text-xl font-bold ${(outperformers.total_profit_loss || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {(outperformers.total_profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(outperformers.total_profit_loss || 0))}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3 flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="Search by PAN or name…"
                      value={outSearch}
                      onChange={(e) => setOutSearch(e.target.value)}
                      className="max-w-sm"
                      data-testid="outperformers-search"
                    />
                    <span className="text-xs text-gray-500">
                      Showing {(() => {
                        const q = outSearch.toLowerCase();
                        const list = (outperformers.clients || []).filter(c => !q ||
                          (c.client_pan || '').toLowerCase().includes(q) ||
                          (c.client_name || '').toLowerCase().includes(q));
                        return list.length;
                      })()} of {outperformers.count || 0} clients
                    </span>
                    <div className="ml-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 border-emerald-300 hover:bg-emerald-50"
                        data-testid="outperformers-csv"
                        onClick={() => {
                          const q = outSearch.toLowerCase();
                          const rows = sortData(
                            (outperformers.clients || []).filter(c => !q ||
                              (c.client_pan || '').toLowerCase().includes(q) ||
                              (c.client_name || '').toLowerCase().includes(q)),
                            outSort,
                          );
                          downloadCsv(rows, [
                            { key: 'client_pan', label: 'PAN' },
                            { key: 'client_name', label: 'Client Name' },
                            { key: 'associate_code', label: 'Associate Code' },
                            { key: 'associate_name', label: 'Associate Name' },
                            { key: 'holdings_count', label: 'Holdings' },
                            { key: 'schemes_count', label: 'Schemes' },
                            { key: 'total_cost', label: 'Cost', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_market', label: 'Market', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_dividends', label: 'Lifetime IDCW', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_redemptions', label: 'Lifetime Redemptions', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'total_market_div', label: 'Market + Div', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'withdrawals_div_market', label: 'Withdrawals + Dividend + Market', fmt: (v, row) => (Number(row?.total_market_div || row?.total_market || 0) + Number(row?.total_redemptions || 0)).toFixed(2) },
                            { key: 'total_profit_loss', label: 'P/L', fmt: v => Number(v || 0).toFixed(2) },
                            { key: 'weighted_xirr_pct', label: 'XIRR %' },
                          ], `Outperformers_${new Date().toISOString().slice(0,10)}.csv`);
                        }}
                      >
                        <Download className="h-4 w-4" />
                        Download CSV
                      </Button>
                    </div>
                  </div>

                  {isMobile && (
                    <MobileDashboardCards
                      rows={sortData(
                        (outperformers.clients || []).filter(c => {
                          if (!outSearch.trim()) return true;
                          const q = outSearch.toLowerCase();
                          return (c.client_pan || '').toLowerCase().includes(q) ||
                                 (c.client_name || '').toLowerCase().includes(q);
                        }),
                        outSort
                      )}
                      emptyMsg="No outperformers found"
                      config={{
                        testIdPrefix: "outperformers",
                        title: (c) => toTitleCase(c.client_name || ""),
                        subtitle: (c) => `PAN: ${c.client_pan || "—"}`,
                        chips: (c) => {
                          const out = [];
                          if (c.associate_code) out.push({ label: c.associate_code, color: "bg-teal-50 text-teal-700 border border-teal-200" });
                          const x = Number(c.weighted_xirr_pct ?? 0);
                          out.push({ label: `${x.toFixed(2)}% XIRR`, color: x > 18 ? "bg-green-100 text-green-800 border border-green-300" : "bg-emerald-100 text-emerald-700 border border-emerald-200" });
                          return out;
                        },
                        stats: [
                          { label: "Cost", value: (c) => mfmtCompact(c.total_cost) },
                          { label: "Withdrawals+Dividend+Market", value: (c) => mfmtCompact((c.total_market_div || c.total_market || 0) + (c.total_redemptions || 0)) },
                          { label: "P/L", value: (c) => `${(c.total_profit_loss || 0) >= 0 ? '+' : '-'}${mfmtCompact(Math.abs(c.total_profit_loss || 0))}`, tone: (c) => (c.total_profit_loss || 0) >= 0 ? "positive" : "negative" },
                          { label: "Holdings", value: (c) => `${c.holdings_count || 0} · ${c.schemes_count || 0} schm` },
                        ],
                        onClick: (c) => toggleDrillDown('outperformers', { ...c, pan: c.client_pan, name: c.client_name }),
                      }}
                    />
                  )}
                  <div className={isMobile ? "hidden" : "border rounded-lg overflow-x-auto max-h-[600px]"}>
                    <Table>
                      <TableHeader className="bg-emerald-50 sticky top-0 z-10">
                        <TableRow>
                          <TableHead className="cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('client_pan', outSort, setOutSort)} data-testid="out-sort-pan"><div className="flex items-center">PAN<SortIcon column="client_pan" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('client_name', outSort, setOutSort)} data-testid="out-sort-name"><div className="flex items-center">Client Name<SortIcon column="client_name" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('associate_code', outSort, setOutSort)} data-testid="out-sort-associate"><div className="flex items-center">Associate<SortIcon column="associate_code" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-center cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('holdings_count', outSort, setOutSort)} data-testid="out-sort-holdings"><div className="flex items-center justify-center">Holdings<SortIcon column="holdings_count" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-center cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('schemes_count', outSort, setOutSort)} data-testid="out-sort-schemes"><div className="flex items-center justify-center">Schemes<SortIcon column="schemes_count" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_cost', outSort, setOutSort)} data-testid="out-sort-cost"><div className="flex items-center justify-end">Cost<SortIcon column="total_cost" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_market_div', outSort, setOutSort)} data-testid="out-sort-market"><div className="flex items-center justify-end">Withdrawals + Dividend + Market<SortIcon column="total_market_div" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_dividends', outSort, setOutSort)} data-testid="out-sort-div"><div className="flex items-center justify-end">Dividend<SortIcon column="total_dividends" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_redemptions', outSort, setOutSort)} data-testid="out-sort-red"><div className="flex items-center justify-end">Redemption<SortIcon column="total_redemptions" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_market', outSort, setOutSort)} data-testid="out-sort-market-only"><div className="flex items-center justify-end">Market Value<SortIcon column="total_market" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_profit_loss', outSort, setOutSort)} data-testid="out-sort-pl"><div className="flex items-center justify-end">P/L<SortIcon column="total_profit_loss" sortConfig={outSort} /></div></TableHead>
                          <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('weighted_xirr_pct', outSort, setOutSort)} data-testid="out-sort-xirr"><div className="flex items-center justify-end">Wtd. XIRR<SortIcon column="weighted_xirr_pct" sortConfig={outSort} /></div></TableHead>
                          <TableHead>Sources</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const _outAll = sortData(
                            (outperformers.clients || []).filter(c => {
                              if (!outSearch.trim()) return true;
                              const q = outSearch.toLowerCase();
                              return (c.client_pan || '').toLowerCase().includes(q) ||
                                     (c.client_name || '').toLowerCase().includes(q);
                            }),
                            outSort
                          );
                          return _outAll.slice((pgOutperformers - 1) * pgSize, pgOutperformers * pgSize).map((c, i) => (
                          <React.Fragment key={`${c.client_pan || c.group_key}-${i}`}>
                          {(() => {
                            const cMapped = { ...c, pan: c.client_pan, name: c.client_name };
                            const drillOpen = openDrillKey === rowKeyForClient('outperformers', cMapped);
                            return (
                              <>
                          <TableRow
                            className={(() => {
                              const x = Number(c.weighted_xirr_pct ?? 0);
                              // Two bands above the floor: emerald 14-18,
                              // deeper green >18 ("elite" performers).
                              if (x > 18) return "bg-green-100 hover:bg-green-200 cursor-pointer";
                              return "bg-emerald-50 hover:bg-emerald-100 cursor-pointer";
                            })()}
                            onClick={() => toggleDrillDown('outperformers', cMapped)}
                            data-testid={`outperformers-row-${i}`}
                          >
                            <TableCell className="font-mono text-xs">
                              <DrillCaret tab="outperformers" client={cMapped} />{' '}
                              {c.client_pan || <span className="text-gray-400 italic">— grouped by name —</span>}
                            </TableCell>
                            <TableCell className="font-medium">{toTitleCase(c.client_name)}</TableCell>
                            <TableCell>{c.associate_code ? <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-mono text-teal-700" title={c.associate_name || ""}>{c.associate_code}</span> : <span className="text-gray-300 text-xs">—</span>}</TableCell>
                            <TableCell className="text-center font-mono">{c.holdings_count}</TableCell>
                            <TableCell className="text-center font-mono text-gray-600">{c.schemes_count}</TableCell>
                            <TableCell className="text-right">{formatCurrency(c.total_cost || 0)}</TableCell>
                            <TableCell className="text-right" title={`Market: ${formatCurrency(c.total_market||0)} + Lifetime IDCW: ${formatCurrency(c.total_dividends||0)} + Lifetime Withdrawals: ${formatCurrency(c.total_redemptions||0)}`}>{formatCurrency((c.total_market_div || c.total_market || 0) + (c.total_redemptions || 0))}</TableCell>
                            <TableCell className="text-right text-emerald-700">{(c.total_dividends || 0) > 0 ? formatCurrency(c.total_dividends) : <span className="text-gray-300">—</span>}</TableCell>
                            <TableCell className="text-right text-indigo-700">{(c.total_redemptions || 0) > 0 ? formatCurrency(c.total_redemptions) : <span className="text-gray-300">—</span>}</TableCell>
                            <TableCell className="text-right text-blue-700">{formatCurrency(c.total_market || 0)}</TableCell>
                            <TableCell className={`text-right ${(c.total_profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(c.total_profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(c.total_profit_loss || 0))}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline" className={(c.weighted_xirr_pct || 0) > 18 ? 'bg-green-100 text-green-800' : 'bg-emerald-50 text-emerald-800'}>
                                {c.weighted_xirr_pct?.toFixed?.(2) ?? '—'}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-gray-600">{(c.sources || []).join(', ')}</TableCell>
                          </TableRow>
                          {drillOpen && (
                            <DrillDownSubRow tab="outperformers" client={cMapped} colSpan={13} columns={[
                              { key: 'folio_no', label: 'Folio' },
                              { key: 'scheme_name', label: 'Scheme' },
                              { key: 'source', label: 'Source' },
                              { key: 'balance_units', label: 'Units', align: 'right', render: v => (v||0).toLocaleString('en-IN', { minimumFractionDigits: 3 }) },
                              { key: 'cost_value', label: 'Cost', align: 'right', render: v => formatCurrency(v) },
                              { key: '_wdm', label: 'Withdrawals + Dividend + Market', align: 'right',
                                render: (_v, row) => formatCurrency((row?.market_value || 0) + (row?.total_dividends || 0) + (row?.total_redemptions || 0)) },
                              { key: 'total_dividends', label: 'Dividend', align: 'right', render: v => (v||0) > 0 ? formatCurrency(v) : '—' },
                              { key: 'total_redemptions', label: 'Redemption', align: 'right', render: v => (v||0) > 0 ? formatCurrency(v) : '—' },
                              { key: 'market_value', label: 'Market Value', align: 'right', render: v => formatCurrency(v) },
                              { key: 'profit_loss', label: 'P/L', align: 'right', render: v => formatCurrency(v), colorFn: v => (v||0) >= 0 ? 'text-green-600' : 'text-red-600' },
                              { key: 'xirr_pct', label: 'Wtd. XIRR', align: 'right', render: v => `${(v||0).toFixed(2)}%` },
                            ]} />
                          )}
                              </>
                            );
                          })()}
                          </React.Fragment>
                        ));
                        })()}
                        {(outperformers.clients || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={13} className="text-center py-12 text-gray-400">
                              No outperformers found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      total={(() => {
                        const q = outSearch.toLowerCase();
                        return (outperformers.clients || []).filter(c => !q ||
                          (c.client_pan || '').toLowerCase().includes(q) ||
                          (c.client_name || '').toLowerCase().includes(q)
                        ).length;
                      })()}
                      page={pgOutperformers}
                      pageSize={pgSize}
                      onPageChange={setPgOutperformers}
                      onPageSizeChange={setPgSize}
                      label="outperformers"
                      testId="outperformers-pagination"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* ════ ELSS by FY Tab (2026-08-08 replacement for ELSS Pending) ════ */}
        <TabsContent value="elss_pending" className="space-y-6" data-testid="elss-by-fy-content">
          <Card className="border-emerald-200">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-emerald-600" />
                    ELSS Investments — All FYs
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    FY-wise breakdown of every client who has invested in ELSS (Sec 80C tax-saver) schemes across all financial years. Click a FY to expand.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Search PAN or name…"
                    value={elssSearch}
                    onChange={(e) => setElssSearch(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded w-64"
                    data-testid="elss-search-input"
                  />
                </div>
              </div>
              {elssPendingLoading ? (
                <div className="py-10 text-center text-gray-500 text-sm">Loading ELSS history…</div>
              ) : !elssPending || (elssPending.financial_years || []).length === 0 ? (
                <div className="py-10 text-center text-gray-500 text-sm">No ELSS investments found.</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                      <div className="text-xs text-emerald-700 font-semibold uppercase">Total Invested (All-Time)</div>
                      <div className="text-2xl font-bold text-emerald-800">{formatCurrency(elssPending.total_all_time || 0)}</div>
                    </div>
                    <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3">
                      <div className="text-xs text-indigo-700 font-semibold uppercase">Financial Years</div>
                      <div className="text-2xl font-bold text-indigo-800">{elssPending.total_fy_count || 0}</div>
                    </div>
                    <div className="border border-gray-200 bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-700 font-semibold uppercase">Latest FY</div>
                      <div className="text-2xl font-bold text-gray-800">{(elssPending.financial_years || [])[0]?.fy_label || "—"}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(elssPending.financial_years || []).map((fy) => {
                      const q = (elssSearch || '').toLowerCase();
                      const filtered = q
                        ? (fy.clients || []).filter((c) =>
                            (c.pan || '').toLowerCase().includes(q) ||
                            (c.name || '').toLowerCase().includes(q))
                        : (fy.clients || []);
                      if (q && filtered.length === 0) return null;
                      const isOpen = elssOpenFYs[fy.fy_label] || false;
                      return (
                        <div key={fy.fy_label} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`elss-fy-${fy.fy_label}`}>
                          <button
                            type="button"
                            onClick={() => setElssOpenFYs((prev) => ({ ...prev, [fy.fy_label]: !prev[fy.fy_label] }))}
                            className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-50 to-white hover:from-emerald-100 transition"
                            data-testid={`elss-fy-toggle-${fy.fy_label}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`text-xs transform transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                              <span className="font-semibold text-gray-800 text-sm">{fy.fy_label}</span>
                              <span className="text-xs text-gray-500">({fy.fy_start} to {fy.fy_end})</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <span className="text-gray-600">{filtered.length} client{filtered.length !== 1 ? 's' : ''}</span>
                              <span className="font-semibold text-emerald-700">{formatCurrency(fy.total_invested)}</span>
                            </div>
                          </button>
                          {isOpen && (
                            <div className="overflow-x-auto max-h-[420px]">
                              <Table>
                                <TableHeader className="sticky top-0 bg-white z-10">
                                  <TableRow className="bg-gray-50">
                                    <TableHead className="text-xs w-8"></TableHead>
                                    <TableHead className="text-xs">Client Name</TableHead>
                                    <TableHead className="text-xs">PAN</TableHead>
                                    <TableHead className="text-xs text-right">Amount Invested</TableHead>
                                    <TableHead className="text-xs text-right">Txn Count</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {filtered.map((c, i) => {
                                    const clientKey = `${fy.fy_label}::${c.pan || c.name}::${i}`;
                                    const clientOpen = elssOpenClients[clientKey] || false;
                                    return (
                                      <React.Fragment key={clientKey}>
                                        <TableRow data-testid={`elss-fy-row-${fy.fy_label}-${i}`} className="hover:bg-gray-50">
                                          <TableCell className="text-xs">
                                            <button
                                              type="button"
                                              onClick={() => setElssOpenClients((prev) => ({ ...prev, [clientKey]: !prev[clientKey] }))}
                                              className="text-emerald-600 hover:text-emerald-800 font-semibold w-5 text-center"
                                              title={clientOpen ? "Hide schemes" : "Show schemes"}
                                              data-testid={`elss-client-toggle-${fy.fy_label}-${i}`}
                                            >
                                              {clientOpen ? '▾' : '▸'}
                                            </button>
                                          </TableCell>
                                          <TableCell className="text-xs max-w-[280px] truncate" title={c.name}>{c.name || '—'}</TableCell>
                                          <TableCell className="text-xs font-mono">{c.pan || '—'}</TableCell>
                                          <TableCell className="text-xs text-right font-semibold text-emerald-700">{formatCurrency(c.invested)}</TableCell>
                                          <TableCell className="text-xs text-right">{c.txn_count}</TableCell>
                                        </TableRow>
                                        {clientOpen && (c.schemes || []).length > 0 && (
                                          <TableRow className="bg-emerald-50/40">
                                            <TableCell colSpan={5} className="p-0">
                                              <div className="pl-10 pr-4 py-2">
                                                <div className="text-[11px] font-semibold text-emerald-800 mb-1">Schemes ({(c.schemes || []).length})</div>
                                                <Table>
                                                  <TableHeader>
                                                    <TableRow className="bg-white">
                                                      <TableHead className="text-[11px]">Scheme</TableHead>
                                                      <TableHead className="text-[11px]">Folio(s)</TableHead>
                                                      <TableHead className="text-[11px] text-right">Invested</TableHead>
                                                      <TableHead className="text-[11px] text-right">Txns</TableHead>
                                                    </TableRow>
                                                  </TableHeader>
                                                  <TableBody>
                                                    {(c.schemes || []).map((s, si) => (
                                                      <TableRow key={`${clientKey}-scheme-${si}`} data-testid={`elss-scheme-row-${fy.fy_label}-${i}-${si}`}>
                                                        <TableCell className="text-[11px] max-w-[420px] truncate" title={s.scheme_name}>{s.scheme_name || '—'}</TableCell>
                                                        <TableCell className="text-[11px] font-mono text-gray-600">{(s.folios || []).join(', ')}</TableCell>
                                                        <TableCell className="text-[11px] text-right font-semibold text-emerald-700">{formatCurrency(s.invested)}</TableCell>
                                                        <TableCell className="text-[11px] text-right">{s.txn_count}</TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ════ Profit Realignment Tab — same template as Reduced Portfolio ════ */}
        <TabsContent value="profit_realignment" className="space-y-6" data-testid="profit-realignment-content">
          <Card className="border-emerald-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-600" />
                  Profit Realignment Clients
                </h3>
                <Badge variant="outline" className="bg-emerald-50">
                  Harvest ₹1.25 L LTCG · {profitRealignment?.fy_label || "Loading…"}
                </Badge>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Clients</div>
                  <div className="text-2xl font-bold text-emerald-700">{profitRealignment?.total_clients || 0}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Total Unrealised Gain</div>
                  <div className="text-xl font-bold text-green-700">
                    {formatCurrency((profitRealignment?.clients || []).reduce((s, c) => s + (c.total_unrealized_gain || 0), 0))}
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Total Sale Value</div>
                  <div className="text-xl font-bold text-blue-700">
                    {formatCurrency((profitRealignment?.clients || []).reduce((s, c) => s + (c.sale_value || 0), 0))}
                  </div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Recommended Harvest</div>
                  <div className="text-xl font-bold text-amber-700">
                    {formatCurrency((profitRealignment?.clients || []).reduce((s, c) => s + (c.recommended_harvest || c.harvested_gain || 0), 0))}
                  </div>
                </div>
              </div>

              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <Input
                  type="text"
                  placeholder="Search by PAN or name…"
                  value={profitSearch}
                  onChange={(e) => setProfitSearch(e.target.value)}
                  className="max-w-sm"
                  data-testid="profit-realignment-search"
                />
                <div className="flex items-center gap-2 ml-auto">
                  <label className="text-xs text-gray-600">Min gain ≥ ₹</label>
                  <input
                    type="number"
                    value={profitMinGain}
                    onChange={(e) => setProfitMinGain(Math.max(0, parseInt(e.target.value) || 0))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded w-28"
                    data-testid="profit-min-gain-input"
                  />
                </div>
                <span className="text-xs text-gray-500">
                  Showing {(() => {
                    const list = (profitRealignment?.clients || []).filter(c => {
                      if (!profitSearch.trim()) return true;
                      const q = profitSearch.toLowerCase();
                      return (c.pan || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
                    });
                    return list.length;
                  })()} of {profitRealignment?.total_clients || 0}
                </span>
              </div>

              {profitRealignmentLoading ? (
                <div className="py-10 text-center text-gray-500 text-sm">Loading clients…</div>
              ) : (
                <>
                {isMobile && (
                  <MobileDashboardCards
                    rows={sortData(
                      (profitRealignment?.clients || []).filter(c => {
                        if (!profitSearch.trim()) return true;
                        const q = profitSearch.toLowerCase();
                        return (c.pan || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
                      }).filter(c => (c.total_unrealized_gain || 0) >= (profitMinGain || 0)),
                      profitSort
                    )}
                    emptyMsg="No clients above the harvest threshold"
                    config={{
                      testIdPrefix: "profit",
                      title: (c) => toTitleCase(c.name || ""),
                      subtitle: (c) => `PAN: ${c.pan || "—"} · ${c.picks_count || 0} funds`,
                      chips: (c) => [{ label: `Harvest ${mfmtCompact(c.recommended_harvest || c.harvested_gain || 0)}`, color: "bg-amber-50 text-amber-700 border border-amber-200" }],
                      stats: [
                        { label: "Equity AUM", value: (c) => mfmtCompact(c.total_market_value) },
                        { label: "Gain", value: (c) => mfmtCompact(c.total_unrealized_gain), tone: () => "positive" },
                        { label: "Headroom", value: (c) => mfmtCompact(c.remaining_headroom) },
                        { label: "Sale Value", value: (c) => mfmtCompact(c.sale_value) },
                      ],
                    }}
                  />
                )}
                <div className={isMobile ? "hidden" : "border rounded-lg overflow-x-auto max-h-[600px]"}>
                  <Table>
                    <TableHeader className="bg-emerald-50 sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('pan', profitSort, setProfitSort)} data-testid="profit-sort-pan"><div className="flex items-center">PAN<SortIcon column="pan" sortConfig={profitSort} /></div></TableHead>
                        <TableHead className="cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('name', profitSort, setProfitSort)} data-testid="profit-sort-name"><div className="flex items-center">Name<SortIcon column="name" sortConfig={profitSort} /></div></TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_market_value', profitSort, setProfitSort)} data-testid="profit-sort-aum"><div className="flex items-center justify-end">Equity AUM<SortIcon column="total_market_value" sortConfig={profitSort} /></div></TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('total_unrealized_gain', profitSort, setProfitSort)} data-testid="profit-sort-gain"><div className="flex items-center justify-end">Unrealised Gain<SortIcon column="total_unrealized_gain" sortConfig={profitSort} /></div></TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('remaining_headroom', profitSort, setProfitSort)} data-testid="profit-sort-headroom"><div className="flex items-center justify-end">FY Headroom<SortIcon column="remaining_headroom" sortConfig={profitSort} /></div></TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('sale_value', profitSort, setProfitSort)} data-testid="profit-sort-sale"><div className="flex items-center justify-end">Sale Value<SortIcon column="sale_value" sortConfig={profitSort} /></div></TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('recommended_harvest', profitSort, setProfitSort)} data-testid="profit-sort-harvest"><div className="flex items-center justify-end">Recommended Harvest<SortIcon column="recommended_harvest" sortConfig={profitSort} /></div></TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-emerald-100" onClick={() => handleSort('picks_count', profitSort, setProfitSort)} data-testid="profit-sort-picks"><div className="flex items-center justify-end">Funds<SortIcon column="picks_count" sortConfig={profitSort} /></div></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortData(
                        (profitRealignment?.clients || []).filter(c => {
                          if (!profitSearch.trim()) return true;
                          const q = profitSearch.toLowerCase();
                          return (c.pan || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
                        }),
                        profitSort
                      ).map((c, i) => (
                        <React.Fragment key={`${c.pan || c.name}-${i}`}>
                          <TableRow
                            className="hover:bg-emerald-50/40 cursor-pointer"
                            onClick={() => setExpandedProfitClient((prev) => ({ ...prev, [i]: !prev[i] }))}
                            data-testid={`profit-row-${i}`}
                          >
                            <TableCell className="font-mono text-xs">
                              <span className="text-gray-500 select-none mr-1">{expandedProfitClient[i] ? "▼" : "▶"}</span>
                              {c.pan || '—'}
                            </TableCell>
                            <TableCell className="font-medium">{c.name || '—'}</TableCell>
                            <TableCell className="text-right">{formatCurrency(c.total_market_value)}</TableCell>
                            <TableCell className="text-right text-green-700 font-semibold">{formatCurrency(c.total_unrealized_gain)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(c.remaining_headroom)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(c.sale_value || 0)}</TableCell>
                            <TableCell className="text-right text-emerald-700 font-bold">{formatCurrency(c.recommended_harvest || c.harvested_gain || 0)}</TableCell>
                            <TableCell className="text-right text-xs text-gray-600">{c.picks_count || 0}</TableCell>
                          </TableRow>
                          {expandedProfitClient[i] && (
                            <TableRow className="bg-emerald-50/30">
                              <TableCell colSpan={8} className="p-0">
                                <div className="px-6 py-3 border-t border-emerald-200">
                                  {(c.picks && c.picks.length > 0) ? (
                                    <div className="overflow-x-auto">
                                      <Table>
                                        <TableHeader className="bg-gray-50">
                                          <TableRow>
                                            <TableHead className="text-[10px] py-1.5">#</TableHead>
                                            <TableHead className="text-[10px] py-1.5">Bucket</TableHead>
                                            <TableHead className="text-[10px] py-1.5">Fund (Scheme)</TableHead>
                                            <TableHead className="text-[10px] py-1.5">Folio</TableHead>
                                            <TableHead className="text-[10px] py-1.5">Lots</TableHead>
                                            <TableHead className="text-[10px] py-1.5 text-right">Units to Sell</TableHead>
                                            <TableHead className="text-[10px] py-1.5 text-right">Current NAV</TableHead>
                                            <TableHead className="text-[10px] py-1.5 text-right">Sale Amount</TableHead>
                                            <TableHead className="text-[10px] py-1.5 text-right">Gain Booked</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {(() => {
                                            // Group picks by (folio_no + scheme_name) — one summary row per scheme.
                                            const groups = [];
                                            const idx = new Map();
                                            c.picks.forEach((p) => {
                                              const k = `${p.folio_no}|${p.scheme_name}`;
                                              if (!idx.has(k)) {
                                                idx.set(k, groups.length);
                                                groups.push({
                                                  folio_no: p.folio_no, scheme_name: p.scheme_name,
                                                  bucket: p.bucket, partial: false,
                                                  units_to_sell: 0, current_nav: p.current_nav || 0,
                                                  sale_value: 0, gain_realized: 0, lots: [],
                                                });
                                              }
                                              const g = groups[idx.get(k)];
                                              g.units_to_sell += (p.units_to_sell || 0);
                                              g.sale_value += (p.sale_value || 0);
                                              g.gain_realized += (p.gain_realized || 0);
                                              g.partial = g.partial || !!p.partial;
                                              g.lots.push(p);
                                            });
                                            return groups.map((g, gi) => (
                                              <TableRow key={`group-${i}-${gi}`} data-testid={`profit-group-${i}-${gi}`}>
                                                <TableCell className="text-[10px] py-1.5">{gi + 1}</TableCell>
                                                <TableCell className="text-[10px] py-1.5">
                                                  <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    {g.bucket?.replace(' Years', 'y')}
                                                  </Badge>
                                                  {g.partial && <span className="ml-1 text-[9px] text-amber-700 bg-amber-50 px-1 rounded">partial</span>}
                                                </TableCell>
                                                <TableCell className="text-[11px] py-1.5 max-w-[320px] truncate" title={g.scheme_name}>{g.scheme_name}</TableCell>
                                                <TableCell className="text-[10px] py-1.5 font-mono">{g.folio_no}</TableCell>
                                                <TableCell className="text-[10px] py-1.5 italic text-gray-500">{g.lots.length} lot{g.lots.length === 1 ? "" : "s"}</TableCell>
                                                <TableCell className="text-[10px] py-1.5 text-right">{g.units_to_sell.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</TableCell>
                                                <TableCell className="text-[10px] py-1.5 text-right">{(g.current_nav || 0).toFixed(4)}</TableCell>
                                                <TableCell className="text-[11px] py-1.5 text-right font-medium">{formatCurrency(g.sale_value)}</TableCell>
                                                <TableCell className="text-[11px] py-1.5 text-right text-emerald-700 font-semibold">{formatCurrency(g.gain_realized)}</TableCell>
                                              </TableRow>
                                            ));
                                          })()}
                                        </TableBody>
                                      </Table>
                                      {c.unfilled_headroom > 100 && (
                                        <div className="mt-2 px-3 py-2 text-[11px] bg-amber-50 border border-amber-200 rounded text-amber-800">
                                          ⚠ Could not fully fill the ₹1.25 L headroom — only {formatCurrency(c.harvested_gain)} available. Remaining unfilled: {formatCurrency(c.unfilled_headroom)}.
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="py-3 text-xs text-gray-500 italic">
                                      No eligible equity lots with realisable gain — headroom unfilled.
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                      {(profitRealignment?.clients || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-12 text-gray-400">
                            No clients found with unrealised equity LTCG above ₹{profitMinGain.toLocaleString('en-IN')}.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unpaid Brokerage Tab */}
        <TabsContent value="unpaid_brokerage" className="space-y-6" data-testid="unpaid-brokerage-content">
          <Card className="border-rose-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-rose-600" />
                  Unpaid Brokerage
                </h3>
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                  {unpaidBrokerage?.wbr77_rows || 0} WBR77 + {unpaidBrokerage?.mfsd205_rows || 0} MFSD205 rows
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-rose-50 rounded-lg p-4" title="Distinct PANs (clients) with at least one unpaid folio/txn">
                  <div className="text-sm text-gray-500">Unpaid Clients (PANs)</div>
                  <div className="text-2xl font-bold text-rose-700">{(unpaidBrokerage?.total_pans || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-gray-500 mt-1">
                    <span className="text-rose-700 font-semibold">{unpaidBrokerage?.pans_never_paid || 0}</span> never paid
                    <span className="mx-1">·</span>
                    <span className="text-amber-700 font-semibold">{unpaidBrokerage?.pans_partial || 0}</span> partial
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4" title="Unique folios in active_units that aren't fully reconciled">
                  <div className="text-sm text-gray-500">Unpaid Folios</div>
                  <div className="text-2xl font-bold text-orange-700">{(unpaidBrokerage?.total_folios || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-gray-500 mt-1">
                    <span className="text-rose-700 font-semibold">{unpaidBrokerage?.folios_never_paid || 0}</span> never paid
                    <span className="mx-1">·</span>
                    <span className="text-amber-700 font-semibold">{unpaidBrokerage?.folios_partial || 0}</span> partial
                  </div>
                </div>
                <div className="bg-violet-50 rounded-lg p-4" title="Total unpaid transactions across all folios">
                  <div className="text-sm text-gray-500">Unpaid Txns</div>
                  <div className="text-2xl font-bold text-violet-700">{(unpaidBrokerage?.total || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-gray-500 mt-1">drill-down per scheme</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">AUM Not Paid For</div>
                  <div className="text-xl font-bold text-amber-700">{formatCurrency(unpaidBrokerage?.total_amount || 0)}</div>
                </div>
              </div>

              {unpaidBrokerage?.note && (
                <div className="mb-3 px-3 py-2 text-[11px] bg-amber-50 border border-amber-200 rounded text-amber-800">
                  ⓘ {unpaidBrokerage.note}
                </div>
              )}

              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <Input
                  type="text"
                  placeholder="Search PAN / name / folio / scheme / txn no…"
                  value={unpaidSearch}
                  onChange={(e) => setUnpaidSearch(e.target.value)}
                  className="max-w-sm"
                  data-testid="unpaid-brokerage-search"
                />
                <div className="flex items-center gap-1 text-xs">
                  <button
                    onClick={() => setUnpaidStatusFilter("all")}
                    className={`px-3 py-1.5 rounded border ${unpaidStatusFilter === "all" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                    data-testid="unpaid-filter-all"
                  >All</button>
                  <button
                    onClick={() => setUnpaidStatusFilter("folio_never_paid")}
                    className={`px-3 py-1.5 rounded border ${unpaidStatusFilter === "folio_never_paid" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"}`}
                    data-testid="unpaid-filter-never-paid"
                  >Never paid</button>
                  <button
                    onClick={() => setUnpaidStatusFilter("txn_unpaid")}
                    className={`px-3 py-1.5 rounded border ${unpaidStatusFilter === "txn_unpaid" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"}`}
                    data-testid="unpaid-filter-partial"
                  >Partial</button>
                </div>
                <button
                  onClick={() => {
                    const pans = unpaidBrokerage?.pan_groups || [];
                    const rows = [];
                    rows.push(["PAN","Client","Sub-Broker","Status","Folio Count","Scheme Count","Txn Count","AUM Not Paid For (PAN)","Folio","Folio Status","Scheme","Scheme Code","AMC","Source","Scheme Txns","AUM Not Paid For (Scheme)","Latest Txn Date","Txn Types","Unpaid Txn Nos"]);
                    pans.forEach(p => {
                      (p.folios || []).forEach(f => {
                        (f.schemes || []).forEach(s => {
                          rows.push([
                            p.pan, p.client_name, p.sub_broker_code,
                            p.paid_status === "folio_never_paid" ? "Never Paid" : "Partial",
                            p.folio_count, p.scheme_count, p.txn_count,
                            p.total_unpaid_amount,
                            f.folio_no,
                            f.paid_status === "folio_never_paid" ? "Never Paid" : "Partial",
                            s.scheme_name, s.scheme_code, s.amc_name, s.source,
                            s.txn_count, s.total_amount,
                            s.latest_txn_date, (s.txn_types || []).join("|"),
                            (s.txns || []).map(t => t.txn_no).filter(Boolean).join("|"),
                          ]);
                        });
                      });
                    });
                    const csv = rows.map(r => r.map(v => {
                      const s = String(v ?? "");
                      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    }).join(",")).join("\n");
                    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = `unpaid_brokerage_${new Date().toISOString().slice(0,10)}.csv`;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  className="px-3 py-1.5 text-xs rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  data-testid="unpaid-export-csv"
                >Export CSV</button>
                <span className="text-xs text-gray-500 ml-auto">
                  {unpaidBrokerage?.cutoff_date
                    ? `Excludes txns dated after ${unpaidBrokerage.cutoff_date}`
                    : "Partial-folio txns exclude 1–31 May"}
                </span>
              </div>

              {unpaidBrokerageLoading ? (
                <div className="py-10 text-center text-gray-500 text-sm">Loading unpaid brokerage…</div>
              ) : (() => {
                // PAN → Folio → Scheme 3-level drill-down (user-requested).
                // Top row = PAN (client). Expand → folios under that PAN.
                // Expand a folio → schemes under that folio (per-scheme
                // aggregate, NOT per-txn — that's noise per user spec).
                const allPans = unpaidBrokerage?.pan_groups || [];
                const q = unpaidSearch.trim().toLowerCase();
                const pans = allPans.filter((p) => {
                  if (unpaidStatusFilter !== "all" && p.paid_status !== unpaidStatusFilter) return false;
                  if (!q) return true;
                  if ([p.pan, p.client_name, p.sub_broker_code].some((v) => (v || "").toString().toLowerCase().includes(q))) return true;
                  // Search inside nested folios + schemes too
                  return (p.folios || []).some(f => {
                    if ((f.folio_no || "").toLowerCase().includes(q)) return true;
                    return (f.schemes || []).some(s => [s.scheme_name, s.scheme_code, s.amc_name]
                      .some((v) => (v || "").toString().toLowerCase().includes(q)));
                  });
                });
                const sorted = sortData(pans, unpaidSort);

                if (sorted.length === 0) {
                  return (
                    <div className="border rounded-lg py-12 text-center text-sm text-gray-400" data-testid="unpaid-empty">
                      {(unpaidBrokerage?.total_pans || 0) === 0
                        ? "Every active folio is reconciled against the latest WBR77/MFSD205 — or no commission file has been uploaded yet."
                        : "No clients match the current search/filter."}
                    </div>
                  );
                }

                return (
                  <>
                  {isMobile && (
                    <MobileDashboardCards
                      rows={sorted}
                      emptyMsg="No unpaid brokerage clients"
                      config={{
                        testIdPrefix: "unpaid",
                        title: (p) => toTitleCase(p.client_name || ""),
                        subtitle: (p) => `PAN: ${p.pan || "—"} · Sub-broker: ${p.sub_broker_code || "—"}`,
                        chips: (p) => [{ label: p.paid_status === "folio_never_paid" ? "Never Paid" : "Partial", color: p.paid_status === "folio_never_paid" ? "bg-rose-100 text-rose-700 border border-rose-200" : "bg-amber-100 text-amber-700 border border-amber-200" }],
                        stats: [
                          { label: "Folios", value: (p) => (p.folio_count || 0).toLocaleString('en-IN') },
                          { label: "Schemes", value: (p) => (p.scheme_count || 0).toLocaleString('en-IN') },
                          { label: "Txns", value: (p) => (p.txn_count || 0).toLocaleString('en-IN') },
                          { label: "Unpaid AUM", value: (p) => mfmtCompact(p.total_unpaid_amount), tone: () => "negative" },
                        ],
                      }}
                    />
                  )}
                  <div className={isMobile ? "hidden border rounded-lg overflow-hidden" : "border rounded-lg overflow-hidden"} data-testid="unpaid-pan-table">
                    <div className="grid grid-cols-12 items-center bg-rose-50 border-b-2 border-rose-200 text-xs font-semibold text-gray-700 px-3 py-2 sticky top-0 z-10">
                      <div className="col-span-1">Status</div>
                      <div className="col-span-2">PAN</div>
                      <div className="col-span-2">Client</div>
                      <div className="col-span-1">Sub-Broker</div>
                      <div className="col-span-1 text-right">Folios</div>
                      <div className="col-span-1 text-right">Schemes</div>
                      <div
                        className="col-span-2 text-right cursor-pointer hover:bg-rose-100"
                        onClick={() => handleSort("txn_count", unpaidSort, setUnpaidSort)}
                        data-testid="unpaid-sort-txn_count"
                      >Txns<SortIcon column="txn_count" sortConfig={unpaidSort} /></div>
                      <div
                        className="col-span-2 text-right cursor-pointer hover:bg-rose-100"
                        onClick={() => handleSort("total_unpaid_amount", unpaidSort, setUnpaidSort)}
                        data-testid="unpaid-sort-total_unpaid_amount"
                      >AUM Not Paid For<SortIcon column="total_unpaid_amount" sortConfig={unpaidSort} /></div>
                    </div>

                    <div className="max-h-[640px] overflow-y-auto">
                      {sorted.map((p, idx) => {
                        const panKey = p.pan || `__noPan_${idx}`;
                        const isPanOpen = !!expandedUnpaidPan[panKey];
                        return (
                          <div key={panKey} className={`border-b border-gray-100 text-xs ${idx % 2 ? "bg-white" : "bg-gray-50/30"}`} data-testid={`unpaid-pan-row-${idx}`}>
                            <div
                              className="grid grid-cols-12 items-center px-3 py-2 cursor-pointer hover:bg-rose-50/40"
                              onClick={() => setExpandedUnpaidPan(s => ({ ...s, [panKey]: !s[panKey] }))}
                              data-testid={`unpaid-pan-toggle-${idx}`}
                            >
                              <div className="col-span-1">
                                <Badge variant="outline" className={
                                  p.paid_status === "folio_never_paid"
                                    ? "bg-rose-100 text-rose-800 border-rose-300"
                                    : "bg-amber-50 text-amber-700 border-amber-200"
                                } title={p.paid_status === "folio_never_paid"
                                  ? "At least one folio for this PAN is absent from WBR77+MFSD205 entirely"
                                  : "All folios for this PAN are commissioned but specific txns are unpaid"}>
                                  {p.paid_status === "folio_never_paid" ? "Never paid" : "Partial"}
                                </Badge>
                              </div>
                              <div className="col-span-2 font-mono">{p.pan || "—"}</div>
                              <div className="col-span-2 font-medium truncate" title={p.client_name}>{p.client_name || "—"}</div>
                              <div className="col-span-1 font-mono text-gray-600 truncate" title={p.sub_broker_code}>{p.sub_broker_code || "—"}</div>
                              <div className="col-span-1 text-right">{(p.folio_count || 0).toLocaleString()}</div>
                              <div className="col-span-1 text-right">{(p.scheme_count || 0).toLocaleString()}</div>
                              <div className="col-span-2 text-right font-semibold">{(p.txn_count || 0).toLocaleString()}</div>
                              <div className="col-span-2 text-right font-semibold text-amber-700">
                                {formatCurrency(p.total_unpaid_amount)}
                                <span className="ml-1 text-rose-700">{isPanOpen ? "▾" : "▸"}</span>
                              </div>
                            </div>

                            {isPanOpen && (
                              <div className="bg-white border-t border-rose-100 px-6 py-3" data-testid={`unpaid-pan-drilldown-${idx}`}>
                                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">
                                  Folios under this PAN — click any folio to see the per-scheme breakdown
                                </div>
                                {(p.folios || []).map((f, fi) => {
                                  const folioKey = `${panKey}__${f.folio_no}`;
                                  const isFolioOpen = !!expandedUnpaidFolio[folioKey];
                                  return (
                                    <div key={folioKey} className="border-l-2 border-rose-200 mb-2" data-testid={`unpaid-folio-row-${idx}-${fi}`}>
                                      <div
                                        className="grid grid-cols-12 items-center px-3 py-1.5 cursor-pointer hover:bg-amber-50/30 bg-amber-50/20"
                                        onClick={() => setExpandedUnpaidFolio(s => ({ ...s, [folioKey]: !s[folioKey] }))}
                                        data-testid={`unpaid-folio-toggle-${idx}-${fi}`}
                                      >
                                        <div className="col-span-1">
                                          <Badge variant="outline" className={`text-[9px] ${
                                            f.paid_status === "folio_never_paid"
                                              ? "bg-rose-50 text-rose-700 border-rose-200"
                                              : "bg-amber-50 text-amber-700 border-amber-200"
                                          }`}>
                                            {f.paid_status === "folio_never_paid" ? "Never paid" : "Partial"}
                                          </Badge>
                                        </div>
                                        <div className="col-span-3 font-mono font-medium">
                                          <span className="text-rose-700 mr-1">{isFolioOpen ? "▾" : "▸"}</span>
                                          {f.folio_no}
                                        </div>
                                        <div className="col-span-2 truncate text-gray-600" title={f.amc_name}>{f.amc_name || "—"}{f.source && <span className="ml-1 text-[10px] text-gray-400">[{f.source}]</span>}</div>
                                        <div className="col-span-1 text-right">{(f.scheme_count || 0).toLocaleString()} sch</div>
                                        <div className="col-span-2 text-right">{(f.txn_count || 0).toLocaleString()} tx</div>
                                        <div className="col-span-3 text-right font-semibold text-amber-700">{formatCurrency(f.total_unpaid_amount)}</div>
                                      </div>

                                      {isFolioOpen && (
                                        <div className="px-6 py-2 bg-white" data-testid={`unpaid-folio-drilldown-${idx}-${fi}`}>
                                          <table className="w-full text-[11px]">
                                            <thead>
                                              <tr className="text-gray-500 border-b">
                                                <th className="text-left py-1 font-medium">Scheme</th>
                                                <th className="text-left py-1 font-medium">Code</th>
                                                <th className="text-left py-1 font-medium">Type</th>
                                                <th className="text-right py-1 font-medium">Txns</th>
                                                <th className="text-left py-1 font-medium">Txn Nos (unpaid)</th>
                                                <th className="text-left py-1 font-medium">Latest Date</th>
                                                <th className="text-right py-1 font-medium">AUM Not Paid For</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(f.schemes || []).map((s, si) => (
                                                <tr key={si} className="border-b border-gray-50 hover:bg-rose-50/30" data-testid={`unpaid-scheme-row-${idx}-${fi}-${si}`}>
                                                  <td className="py-1 truncate max-w-md" title={s.scheme_name}>{s.scheme_name || "—"}</td>
                                                  <td className="py-1 font-mono text-gray-600">{s.scheme_code || "—"}</td>
                                                  <td className="py-1">
                                                    {(s.txn_types || []).slice(0, 2).map((t, ti) => (
                                                      <Badge key={ti} variant="outline" className={`text-[9px] mr-1 ${
                                                        /switch/i.test(t) ? "bg-blue-50 text-blue-700 border-blue-200"
                                                        : /stp/i.test(t) ? "bg-violet-50 text-violet-700 border-violet-200"
                                                        : /sip/i.test(t) ? "bg-amber-50 text-amber-700 border-amber-200"
                                                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                      }`}>{t}</Badge>
                                                    ))}
                                                  </td>
                                                  <td className="py-1 text-right">{(s.txn_count || 0).toLocaleString()}</td>
                                                  <td className="py-1 font-mono text-[10px] text-gray-700">
                                                    {/* Surfaces the exact unpaid transaction numbers per scheme.
                                                        Renders EVERY txn_no as an inline chip (no slicing / no "+N" hover) — the user explicitly
                                                        wants every entry visible and not clubbed when expanded. The wrapping container allows
                                                        the chips to flow over multiple lines for high-txn-count schemes (SIPs over many years). */}
                                                    {(s.txns || []).length === 0 ? (
                                                      <span className="text-gray-300">—</span>
                                                    ) : (
                                                      <div className="flex flex-wrap gap-1">
                                                        {(s.txns || []).map((t, ti) => (
                                                          <span
                                                            key={ti}
                                                            className="inline-block px-1 py-0.5 rounded bg-rose-50 border border-rose-200"
                                                            title={`${t.txn_no} • ${t.txn_date || ""} • ${formatCurrency(t.txn_amount || 0)} • ${t.txn_type || ""}`}
                                                            data-testid={`unpaid-txn-no-${t.txn_no}`}
                                                          >{t.txn_no}</span>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </td>
                                                  <td className="py-1">{s.latest_txn_date || "—"}</td>
                                                  <td className="py-1 text-right font-medium text-amber-700">{formatCurrency(s.total_amount)}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="px-3 py-2 text-[10px] text-gray-400 border-t bg-gray-50">
                      Showing {sorted.length.toLocaleString()} of {(unpaidBrokerage?.total_pans || 0).toLocaleString()} clients · {(unpaidBrokerage?.total_folios || 0).toLocaleString()} folios · {(unpaidBrokerage?.total || 0).toLocaleString()} unpaid txns
                    </div>
                  </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Historical Data Tab — folios with Transfer In but no WBR2A history */}
        <TabsContent value="historical_data" className="space-y-6" data-testid="historical-data-content">
          <Card className="border-sky-200">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FileWarning className="h-5 w-5 text-sky-600" />
                    Historical Data Gaps
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {historicalData
                      ? (
                        <>
                          <span className="font-medium">{historicalData.count}</span> folios · <span className="font-medium">{historicalData.unique_clients}</span> clients · <span className="font-medium">{(historicalData.total_units_missing || 0).toLocaleString('en-IN', {maximumFractionDigits: 2})}</span> units of historical gap
                          {historicalData.counts_by_type ? (
                            <span className="block text-[11px] text-gray-400 mt-0.5">
                              {historicalData.counts_by_type.transfer_in_no_history ? <>· TI w/o WBR2A: {historicalData.counts_by_type.transfer_in_no_history} </> : null}
                              {historicalData.counts_by_type.partial ? <>· Partial WBR2A: {historicalData.counts_by_type.partial} </> : null}
                            </span>
                          ) : null}
                        </>
                      )
                      : "Folios where a Transfer In (TICOB) was detected but the matching WBR2A historical purchases are missing or partial — re-upload WBR2A for these folios to backfill purchase history."}
                  </p>
                </div>
                <Input
                  placeholder="Search by PAN / name / folio / scheme..."
                  value={historicalSearch}
                  onChange={(e) => setHistoricalSearch(e.target.value)}
                  className="w-72"
                  data-testid="historical-data-search"
                />
              </div>

              {historicalLoading ? (
                <div className="text-center py-12 text-gray-500">Loading historical data gaps...</div>
              ) : !historicalData || !historicalData.rows?.length ? (
                <div className="text-center py-12 text-gray-500">
                  No historical data gaps detected — every Transfer In has matching WBR2A history.
                </div>
              ) : (
                <>
                {isMobile && (
                  <MobileDashboardCards
                    rows={(historicalData.rows || []).filter(r => {
                      if (!historicalSearch) return true;
                      const q = historicalSearch.toLowerCase();
                      return (
                        (r.client_pan || "").toLowerCase().includes(q)
                        || (r.client_name || "").toLowerCase().includes(q)
                        || (r.folio_no || "").toLowerCase().includes(q)
                        || (r.scheme_name || "").toLowerCase().includes(q)
                        || (r.sub_broker_code || "").toLowerCase().includes(q)
                        || (r.sub_broker_name || "").toLowerCase().includes(q)
                        || (r.gap_type || "").toLowerCase().includes(q)
                      );
                    })}
                    emptyMsg="No historical data gaps"
                    config={{
                      testIdPrefix: "historical",
                      title: (r) => `${toTitleCase(r.client_name || "")} · ${r.folio_no || ""}`,
                      subtitle: (r) => `${r.scheme_name || "—"}`,
                      chips: (r) => {
                        const gapType = r.gap_type || 'transfer_in_no_history';
                        const gapLabel = ({ transfer_in_no_history: 'TI w/o WBR2A', partial: 'Partial WBR2A' })[gapType] || gapType;
                        const out = [{ label: gapLabel, color: gapType === 'partial' ? "bg-amber-100 text-amber-700 border border-amber-200" : "bg-gray-100 text-gray-700 border border-gray-200" }];
                        if (r.source) out.push({ label: r.source, color: "bg-sky-50 text-sky-700 border border-sky-200" });
                        return out;
                      },
                      stats: [
                        { label: "Gap Units", value: (r) => (r.gap_units || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }), tone: () => "negative" },
                        { label: "TI Units", value: (r) => (r.transfer_in_units || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }) },
                        { label: "Active", value: (r) => (r.wbr2a_covered_units || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }) },
                        { label: "First TI", value: (r) => r.first_transfer_in_date ? new Date(r.first_transfer_in_date).toLocaleDateString('en-IN') : '—' },
                      ],
                    }}
                  />
                )}
                <div className={isMobile ? "hidden" : "border rounded-lg overflow-x-auto"}>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">PAN</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Client</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Sub-broker</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Folio</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Scheme</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Gap type</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">Gap Units</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">TI Units</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">WBR2A / Active</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">First TI</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(historicalData.rows || [])
                        .filter(r => {
                          if (!historicalSearch) return true;
                          const q = historicalSearch.toLowerCase();
                          return (
                            (r.client_pan || "").toLowerCase().includes(q)
                            || (r.client_name || "").toLowerCase().includes(q)
                            || (r.folio_no || "").toLowerCase().includes(q)
                            || (r.scheme_name || "").toLowerCase().includes(q)
                            || (r.sub_broker_code || "").toLowerCase().includes(q)
                            || (r.sub_broker_name || "").toLowerCase().includes(q)
                            || (r.gap_type || "").toLowerCase().includes(q)
                          );
                        })
                        .map((r, i) => {
                          const gapType = r.gap_type || 'transfer_in_no_history';
                          const gapLabel = ({
                            transfer_in_no_history: 'TI w/o WBR2A',
                            partial: 'Partial WBR2A',
                          })[gapType] || gapType;
                          const gapVariant = gapType === 'partial'
                            ? 'secondary'
                            : 'outline';
                          return (
                          <tr key={`${r.client_pan}-${r.folio_no}-${r.product_code}-${i}`} className="border-b hover:bg-sky-50" data-testid={`historical-row-${i}`}>
                            <td className="px-3 py-2 font-mono text-xs">{r.client_pan || '—'}</td>
                            <td className="px-3 py-2">{r.client_name || '—'}</td>
                            <td className="px-3 py-2 text-xs">
                              {r.sub_broker_code ? (
                                <span title={r.sub_broker_name || ""}>
                                  <span className="font-mono">{r.sub_broker_code}</span>
                                  {r.sub_broker_name ? <span className="text-gray-500"> · {r.sub_broker_name}</span> : null}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{r.folio_no}</td>
                            <td className="px-3 py-2 max-w-md truncate" title={r.scheme_name}>{r.scheme_name}</td>
                            <td className="px-3 py-2 text-xs">
                              <Badge variant={gapVariant} className="text-[10px]" data-testid={`historical-gap-type-${i}`}>{gapLabel}</Badge>
                            </td>
                            <td className="px-3 py-2 text-right font-medium">{(r.gap_units || 0).toLocaleString('en-IN', {maximumFractionDigits: 4})}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{(r.transfer_in_units || 0).toLocaleString('en-IN', {maximumFractionDigits: 4})}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{(r.wbr2a_covered_units || 0).toLocaleString('en-IN', {maximumFractionDigits: 4})}</td>
                            <td className="px-3 py-2 text-xs text-gray-600">{r.first_transfer_in_date ? new Date(r.first_transfer_in_date).toLocaleDateString('en-IN') : '—'}</td>
                            <td className="px-3 py-2 text-xs"><Badge variant="outline" className="text-xs">{r.source}</Badge></td>
                          </tr>
                        );})}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Mismatch Tab (Unit Mismatch — folio granularity) */}
        <TabsContent value="mismatch" className="space-y-6" data-testid="mismatch-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Unit Mismatch Folios
              </CardTitle>
              <p className="text-xs text-gray-500">
                Folios where <b>transaction_master</b> active units don't match <b>aum_master</b> holdings.
                Source of truth for Portfolio &gt; Unit Mismatch. Auto-refreshed on every TM rebuild.
              </p>
            </CardHeader>
            <CardContent>
              {mismatchLoading ? (
                <div className="py-10 text-center text-gray-500 text-sm">Loading mismatch folios…</div>
              ) : !mismatchData || (mismatchData.mismatches || []).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>All folios reconcile. No mismatches detected.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <Input
                      placeholder="Search by PAN / client name / folio / scheme…"
                      value={mismatchSearch}
                      onChange={(e) => setMismatchSearch(e.target.value)}
                      className="max-w-md"
                      data-testid="mismatch-search"
                    />
                    <div className="text-xs text-gray-500 ml-auto">
                      <b>{mismatchData.total || 0}</b> folios · <b>{mismatchData.unique_clients || 0}</b> clients
                      · excess holdings: <b>{mismatchData.excess_holdings_count || 0}</b>
                      · excess txn: <b>{mismatchData.excess_transactions_count || 0}</b>
                    </div>
                  </div>
                  {(() => {
                    const q = mismatchSearch.trim().toLowerCase();
                    const allRows = mismatchData.mismatches || [];
                    // Count entries per capsule type (used for chip
                    // badges + "All" label).
                    const capsuleCounts = allRows.reduce((acc, m) => {
                      const t = m.pending_txn_type || "Other";
                      acc[t] = (acc[t] || 0) + 1;
                      return acc;
                    }, {});
                    // Colour palette for the SIP/STP/SWP/Purchase/Sell/
                    // Switch capsules — keys are the actual `pending_
                    // txn_type` labels emitted by the backend.
                    const capsuleColor = (t) => ({
                      SIP:      "bg-sky-100 text-sky-700 border-sky-200",
                      STP:      "bg-cyan-100 text-cyan-700 border-cyan-200",
                      SWP:      "bg-orange-100 text-orange-700 border-orange-200",
                      Purchase: "bg-indigo-100 text-indigo-700 border-indigo-200",
                      Sell:     "bg-rose-100 text-rose-700 border-rose-200",
                      Switch:   "bg-violet-100 text-violet-700 border-violet-200",
                      Txn:      "bg-gray-100 text-gray-700 border-gray-200",
                      Other:    "bg-slate-100 text-slate-700 border-slate-200",
                    }[t] || "bg-gray-100 text-gray-700 border-gray-200");
                    // Apply capsule filter first, then search filter.
                    const capsuleFiltered = allRows.filter((m) => {
                      if (mismatchCapsuleFilter === "all") return true;
                      if (mismatchCapsuleFilter === "Other") return !m.pending_txn_type;
                      return m.pending_txn_type === mismatchCapsuleFilter;
                    });
                    const filtered = capsuleFiltered.filter((m) => {
                      if (!q) return true;
                      return (
                        (m.client_pan || "").toLowerCase().includes(q) ||
                        (m.client_name || "").toLowerCase().includes(q) ||
                        (m.folio_no || "").toLowerCase().includes(q) ||
                        (m.scheme_name || "").toLowerCase().includes(q)
                      );
                    });
                    const capsuleOrder = ["SIP", "STP", "SWP", "Purchase", "Sell", "Switch", "Txn", "Other"];
                    const paged = filtered.slice((pgMismatch - 1) * pgSize, pgMismatch * pgSize);
                    // Capsule filter chip strip (rendered above the table)
                    const chipStrip = (
                      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="mismatch-capsule-filter">
                        <button
                          type="button"
                          onClick={() => { setMismatchCapsuleFilter("all"); setPgMismatch(1); }}
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${mismatchCapsuleFilter === "all" ? "bg-red-600 text-white border-red-700" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                          data-testid="mismatch-filter-all"
                        >
                          All · {allRows.length}
                        </button>
                        {capsuleOrder.map((cap) => {
                          const n = capsuleCounts[cap] || 0;
                          if (!n) return null;
                          const active = mismatchCapsuleFilter === cap;
                          const base = capsuleColor(cap);
                          return (
                            <button
                              key={cap}
                              type="button"
                              onClick={() => { setMismatchCapsuleFilter(cap); setPgMismatch(1); }}
                              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${active ? "ring-2 ring-offset-1 ring-red-500 " + base : base + " opacity-80 hover:opacity-100"}`}
                              data-testid={`mismatch-filter-${cap.toLowerCase()}`}
                            >
                              {cap} · {n}
                            </button>
                          );
                        })}
                      </div>
                    );
                    if (isMobile) {
                      return (
                        <>
                        {chipStrip}
                        <MobileDashboardCards
                          rows={paged}
                          emptyMsg="No mismatches match your search"
                          config={{
                            testIdPrefix: "mismatch",
                            title: (m) => toTitleCase(m.client_name || ""),
                            subtitle: (m) => `PAN: ${m.client_pan || "—"} · Folio: ${m.folio_no || "—"}`,
                            chips: (m) => {
                              const diff = Number(m.difference || 0);
                              const chips = [{
                                label: diff > 0 ? `+${diff.toFixed(2)}u excess active` : `${diff.toFixed(2)}u excess holdings`,
                                color: diff > 0 ? "bg-orange-100 text-orange-700 border border-orange-200" : "bg-rose-100 text-rose-700 border border-rose-200",
                              }];
                              if (m.is_transfer_out) chips.unshift({ label: "TO", color: "bg-amber-100 text-amber-700 border border-amber-200" });
                              if (m.pending_txn_type) chips.unshift({ label: m.pending_txn_type, color: `${capsuleColor(m.pending_txn_type)} border` });
                              if (m.is_historical_data) chips.unshift({ label: "HIST", color: "bg-emerald-100 text-emerald-700 border border-emerald-200" });
                              return chips;
                            },
                            stats: [
                              { label: "Holdings (AUM)", value: (m) => Number(m.balance_from_holdings || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }) },
                              { label: "Active (TM)", value: (m) => Number(m.active_units_txn || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }) },
                              { label: "Diff", value: (m) => Number(m.difference || 0).toFixed(2), tone: (m) => (Number(m.difference || 0) >= 0 ? "negative" : "positive") },
                              { label: "Source", value: (m) => (m.source || "").toString().split(",").slice(0, 2).join(", ") || "—" },
                            ],
                          }}
                        />
                        <TablePagination
                          total={filtered.length}
                          page={pgMismatch}
                          pageSize={pgSize}
                          onPageChange={setPgMismatch}
                          onPageSizeChange={setPgSize}
                          label="mismatched folios"
                          testId="mismatch-pagination"
                        />
                        </>
                      );
                    }
                    return (
                      <>
                      {chipStrip}
                      <div className="border rounded-lg overflow-x-auto max-h-[600px]" data-testid="mismatch-table-wrap">
                        <Table>
                          <TableHeader className="bg-red-50 sticky top-0 z-10">
                            <TableRow>
                              <TableHead className="w-12">#</TableHead>
                              <TableHead>Client Name</TableHead>
                              <TableHead>PAN</TableHead>
                              <TableHead className="text-right">Folios</TableHead>
                              <TableHead className="text-right">Holdings (AUM)</TableHead>
                              <TableHead className="text-right">Active (TM)</TableHead>
                              <TableHead className="text-right">Difference</TableHead>
                              <TableHead>Sources</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {/* 2026-07-05 user directive — group by PAN. */}
                            {(() => {
                              // Group `paged` by client_pan → aggregate
                              // totals + folio list. Falls back to client
                              // name when PAN missing.
                              const groups = new Map();
                              paged.forEach((m) => {
                                const key = (m.client_pan || `name-${m.client_name || ""}`).trim();
                                if (!groups.has(key)) {
                                  groups.set(key, {
                                    pan: m.client_pan || "",
                                    name: m.client_name || "",
                                    folios: [],
                                    holdings_total: 0,
                                    active_total: 0,
                                    diff_total: 0,
                                    sources: new Set(),
                                  });
                                }
                                const g = groups.get(key);
                                g.folios.push(m);
                                g.holdings_total += Number(m.balance_from_holdings || 0);
                                g.active_total   += Number(m.active_units_txn   || 0);
                                g.diff_total     += Number(m.difference          || 0);
                                (String(m.source || "").split(",")).forEach(s => {
                                  const t = s.trim();
                                  if (t) g.sources.add(t);
                                });
                              });
                              const grouped = Array.from(groups.values());
                              return grouped.map((g, idx) => {
                                const rowKey = g.pan || `name-${idx}`;
                                const isExpanded = expandedMismatch === rowKey;
                                return (
                                  <React.Fragment key={`msg-${rowKey}-${idx}`}>
                                    <TableRow
                                      className="hover:bg-gray-50 cursor-pointer"
                                      onClick={() => setExpandedMismatch(isExpanded ? null : rowKey)}
                                      data-testid={`mismatch-row-${rowKey}`}
                                    >
                                      <TableCell className="font-mono text-xs text-gray-500">
                                        <span className="inline-flex items-center gap-1">
                                          {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                          {idx + 1}
                                        </span>
                                      </TableCell>
                                      <TableCell className="font-medium">{toTitleCase(g.name || "")}</TableCell>
                                      <TableCell className="font-mono text-xs">{g.pan || "—"}</TableCell>
                                      <TableCell className="text-right font-mono">{g.folios.length}</TableCell>
                                      <TableCell className="text-right font-mono">{g.holdings_total.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</TableCell>
                                      <TableCell className="text-right font-mono">{g.active_total.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</TableCell>
                                      <TableCell className={`text-right font-mono font-semibold ${g.diff_total >= 0 ? "text-orange-700" : "text-rose-700"}`}>
                                        {g.diff_total > 0 ? "+" : ""}{g.diff_total.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                                      </TableCell>
                                      <TableCell className="text-xs">{Array.from(g.sources).join(", ") || "—"}</TableCell>
                                    </TableRow>
                                    {isExpanded && g.folios.length > 0 && (
                                      <TableRow className="bg-gray-50/50">
                                        <TableCell colSpan={8} className="p-0">
                                          <div className="px-6 py-3">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Folio + Scheme breakdown</div>
                                            <table className="w-full text-xs" data-testid={`mismatch-drilldown-${rowKey}`}>
                                              <thead>
                                                <tr className="text-gray-600">
                                                  <th className="text-left py-1 font-medium">Folio</th>
                                                  <th className="text-left py-1 font-medium">Scheme</th>
                                                  <th className="text-left py-1 font-medium">Tags</th>
                                                  <th className="text-right py-1 font-medium">Holdings (AUM)</th>
                                                  <th className="text-right py-1 font-medium">Active (TM)</th>
                                                  <th className="text-right py-1 font-medium">Difference</th>
                                                  <th className="text-left py-1 font-medium">Source</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {g.folios.map((m, fi) => (
                                                  <tr key={fi} className="border-t border-gray-200">
                                                    <td className="py-1 font-mono">{m.folio_no || "—"}</td>
                                                    <td className="py-1">{m.scheme_name || "—"}</td>
                                                    <td className="py-1">
                                                      <span className="inline-flex items-center gap-1 flex-wrap">
                                                        {m.is_transfer_out && (
                                                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">TO</span>
                                                        )}
                                                        {m.pending_txn_type && (
                                                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${capsuleColor(m.pending_txn_type)}`}>{m.pending_txn_type}</span>
                                                        )}
                                                        {m.is_historical_data && (
                                                          <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">HIST</span>
                                                        )}
                                                      </span>
                                                    </td>
                                                    <td className="py-1 text-right font-mono">{Number(m.balance_from_holdings || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</td>
                                                    <td className="py-1 text-right font-mono">{Number(m.active_units_txn || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</td>
                                                    <td className={`py-1 text-right font-mono font-semibold ${Number(m.difference || 0) >= 0 ? "text-orange-700" : "text-rose-700"}`}>
                                                      {Number(m.difference || 0) > 0 ? "+" : ""}{Number(m.difference || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                                                    </td>
                                                    <td className="py-1 text-xs">{m.source || "—"}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              });
                            })()}
                          </TableBody>
                        </Table>
                      </div>
                      <TablePagination
                        total={filtered.length}
                        page={pgMismatch}
                        pageSize={pgSize}
                        onPageChange={setPgMismatch}
                        onPageSizeChange={setPgSize}
                        label="mismatched folios"
                        testId="mismatch-pagination"
                      />
                      </>
                    );
                  })()}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Family AUM Leaderboard Tab */}
        <TabsContent value="family_aum" className="space-y-6" data-testid="family-aum-content">
          <Card className="border-amber-200">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Crown className="h-5 w-5 text-amber-600" />
                    Family AUM Leaderboard
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {familyAum
                      ? `${familyAum.count} families · ₹${(familyAum.total_aum/10000000).toFixed(2)} Cr combined AUM`
                      : "Group Heads ranked by combined family book — quick retention signal."}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Min members:</label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={familyMinMembers}
                      onChange={(e) => setFamilyMinMembers(Math.max(1, parseInt(e.target.value || "1", 10)))}
                      className="h-8 w-16 text-xs"
                      data-testid="family-min-members"
                    />
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-3 w-3 text-gray-400" />
                    <Input
                      placeholder="Search head name or PAN…"
                      value={familySearch}
                      onChange={(e) => setFamilySearch(e.target.value)}
                      className="h-8 w-56 pl-7 text-xs"
                      data-testid="family-search"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Export currently-visible filtered rows to CSV.
                      const rows = (familyAum?.families || [])
                        .filter(f => {
                          if (!familySearch) return true;
                          const q = familySearch.toLowerCase();
                          return (f.group_head_name || "").toLowerCase().includes(q)
                              || (f.group_head_pan || "").toLowerCase().includes(q)
                              || (f.associate_code || "").toLowerCase().includes(q)
                              || (f.associate_name || "").toLowerCase().includes(q);
                        });
                      if (!rows.length) {
                        toast.info("No rows to export");
                        return;
                      }
                      const header = ["Rank", "Group Head", "PAN", "Associate Code", "Associate Name", "Members", "Combined AUM", "Combined Cost", "P/L", "Weighted XIRR %", "Holdings"];
                      const lines = [header.join(",")];
                      for (const r of rows) {
                        lines.push([
                          r.rank,
                          `"${(r.group_head_name||"").replace(/"/g,'""')}"`,
                          r.group_head_pan,
                          r.associate_code || "",
                          `"${(r.associate_name||"").replace(/"/g,'""')}"`,
                          r.member_count,
                          r.total_market,
                          r.total_cost,
                          r.total_profit_loss,
                          r.weighted_xirr_pct,
                          r.holdings_count,
                        ].join(","));
                      }
                      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `Family_AUM_Leaderboard_${new Date().toISOString().slice(0,10)}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      a.remove();
                    }}
                    className="gap-2"
                    data-testid="family-aum-export"
                  >
                    <Download className="h-4 w-4" />
                    CSV
                  </Button>
                </div>
              </div>

              {familyAumLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                  <span className="ml-3 text-sm text-gray-500">Loading families…</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50">
                        <TableHead className="w-10"></TableHead>
                        <TableHead className="w-16">Rank</TableHead>
                        <TableHead
                          className="cursor-pointer hover:bg-amber-100"
                          onClick={() => setFamilySort({
                            column: 'group_head_name',
                            direction: familySort.column === 'group_head_name' && familySort.direction === 'asc' ? 'desc' : 'asc',
                          })}
                        >
                          Group Head <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                        </TableHead>
                        <TableHead
                          className="cursor-pointer hover:bg-amber-100"
                          onClick={() => setFamilySort({
                            column: 'associate_code',
                            direction: familySort.column === 'associate_code' && familySort.direction === 'asc' ? 'desc' : 'asc',
                          })}
                        >
                          Associate <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                        </TableHead>
                        <TableHead
                          className="text-right cursor-pointer hover:bg-amber-100"
                          onClick={() => setFamilySort({
                            column: 'member_count',
                            direction: familySort.column === 'member_count' && familySort.direction === 'desc' ? 'asc' : 'desc',
                          })}
                        >
                          Members <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                        </TableHead>
                        <TableHead
                          className="text-right cursor-pointer hover:bg-amber-100"
                          onClick={() => setFamilySort({
                            column: 'total_market',
                            direction: familySort.column === 'total_market' && familySort.direction === 'desc' ? 'asc' : 'desc',
                          })}
                        >
                          Combined AUM <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                        </TableHead>
                        <TableHead
                          className="text-right cursor-pointer hover:bg-amber-100"
                          onClick={() => setFamilySort({
                            column: 'total_profit_loss',
                            direction: familySort.column === 'total_profit_loss' && familySort.direction === 'desc' ? 'asc' : 'desc',
                          })}
                        >
                          P/L <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                        </TableHead>
                        <TableHead
                          className="text-right cursor-pointer hover:bg-amber-100"
                          onClick={() => setFamilySort({
                            column: 'weighted_xirr_pct',
                            direction: familySort.column === 'weighted_xirr_pct' && familySort.direction === 'desc' ? 'asc' : 'desc',
                          })}
                        >
                          XIRR % <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                        </TableHead>
                        <TableHead className="w-32 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const rows = (familyAum?.families || [])
                          .filter(f => {
                            if (!familySearch) return true;
                            const q = familySearch.toLowerCase();
                            return (f.group_head_name || "").toLowerCase().includes(q)
                                || (f.group_head_pan || "").toLowerCase().includes(q)
                                || (f.associate_code || "").toLowerCase().includes(q)
                                || (f.associate_name || "").toLowerCase().includes(q);
                          });
                        const sortKey = familySort.column;
                        const sortDir = familySort.direction === 'asc' ? 1 : -1;
                        rows.sort((a, b) => {
                          const va = a[sortKey];
                          const vb = b[sortKey];
                          if (typeof va === "string") return sortDir * String(va || "").localeCompare(String(vb || ""));
                          return sortDir * ((va || 0) - (vb || 0));
                        });
                        return rows.map(f => {
                          const isOpen = expandedFamily === f.group_head_pan;
                          const xirrColor = (f.weighted_xirr_pct || 0) >= 12
                            ? "text-green-700"
                            : (f.weighted_xirr_pct || 0) >= 8
                              ? "text-amber-700"
                              : "text-red-700";
                          const plColor = (f.total_profit_loss || 0) >= 0 ? "text-green-700" : "text-red-700";
                          return (
                            <React.Fragment key={f.group_head_pan}>
                              <TableRow
                                className={`hover:bg-amber-50/40 ${isOpen ? "bg-amber-50/30" : ""}`}
                                data-testid={`family-row-${f.group_head_pan}`}
                              >
                                <TableCell>
                                  <button
                                    onClick={() => setExpandedFamily(isOpen ? null : f.group_head_pan)}
                                    className="text-gray-500 hover:text-amber-600"
                                    data-testid={`family-expand-${f.group_head_pan}`}
                                  >
                                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </button>
                                </TableCell>
                                <TableCell className="font-semibold text-gray-700">#{f.rank}</TableCell>
                                <TableCell>
                                  <div
                                    className="font-medium text-gray-800 cursor-help"
                                    title={`PAN: ${f.group_head_pan}`}
                                    data-testid={`family-head-name-${f.group_head_pan}`}
                                  >
                                    {f.group_head_name || "—"}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {f.associate_code ? (
                                    <div
                                      className="text-xs"
                                      title={f.associate_name || ""}
                                      data-testid={`family-associate-${f.group_head_pan}`}
                                    >
                                      <Badge variant="outline" className="font-mono border-teal-300 text-teal-700 bg-teal-50">
                                        {f.associate_code}
                                      </Badge>
                                    </div>
                                  ) : (
                                    <span className="text-gray-300 text-xs">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50">
                                    {f.member_count}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {formatCurrency(f.total_market)}
                                </TableCell>
                                <TableCell className={`text-right font-medium ${plColor}`}>
                                  {formatCurrency(f.total_profit_loss)}
                                </TableCell>
                                <TableCell className={`text-right font-semibold ${xirrColor}`}>
                                  {(f.weighted_xirr_pct || 0).toFixed(2)}%
                                </TableCell>
                                <TableCell className="text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[11px] gap-1"
                                    onClick={() => navigate(`/portfolio?client=${encodeURIComponent(f.group_head_pan)}`)}
                                    data-testid={`family-open-portfolio-${f.group_head_pan}`}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    Portfolio
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {isOpen && (
                                <TableRow className="bg-amber-50/20">
                                  <TableCell colSpan={9} className="py-3">
                                    <div className="ml-10 mr-4">
                                      <div className="text-[11px] font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                                        Members breakdown · {f.holdings_count} holdings
                                      </div>
                                      <Table>
                                        <TableHeader>
                                          <TableRow className="bg-white/60">
                                            <TableHead className="h-8 text-[11px]">Member</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Cost</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Market</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">P/L</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">XIRR %</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Holdings</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {(f.members || []).map(m => (
                                            <TableRow key={m.pan} className="text-xs">
                                              <TableCell>
                                                <div className="font-medium text-gray-800">{m.name || "—"}</div>
                                                <div className="text-[10px] font-mono text-gray-400">{m.pan}</div>
                                              </TableCell>
                                              <TableCell className="text-right">{formatCurrency(m.cost)}</TableCell>
                                              <TableCell className="text-right font-medium">{formatCurrency(m.market)}</TableCell>
                                              <TableCell className={`text-right ${(m.profit_loss||0) >= 0 ? "text-green-700" : "text-red-700"}`}>
                                                {formatCurrency(m.profit_loss)}
                                              </TableCell>
                                              <TableCell className="text-right">{(m.weighted_xirr_pct || 0).toFixed(2)}%</TableCell>
                                              <TableCell className="text-right">{m.holdings_count}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                      {(!familyAum || (familyAum.families || []).length === 0) && (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-12 text-gray-400">
                            No families found. Map clients to a Group Head from the Client-wise summary to populate this leaderboard.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

